// /**
//  * IcpProfilesPage.tsx — ICP profiles CRUD + AI Suggest + Auto-Discover.
//  *
//  * Layout  : card grid (3-col responsive), matching the Next.js reference.
//  * API     : /api/v1/icp-profiles (list, create, update, delete)
//  *           /api/v1/icp-profiles/suggest  (AI suggest fields)
//  *           /api/v1/icp-profiles/auto-discover (derive ICP from prospects)
//  *
//  * ICP-1  ✓ Auto-Discover dialog — 2-tab (website / description), save toggle,
//  *            result preview, per-persona save.
//  * ICP-2  ✓ Correct form fields: name, persona, companyType, topObjections (3),
//  *            painPoints (3), valueProps (3), senderRole, senderCompany,
//  *            senderOffer, proofMetric.
//  * ICP-3  ✓ Card grid display — all fields shown with badge truncation + expand.
//  * ICP-4  ✓ Edit dialog pre-populated with existing values.
//  * ICP-5  ✓ Delete with confirmation dialog.
//  * ICP-6  ✓ LLM config warning banner when no active LLM.
//  */
// import { useState } from "react";
// import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// import {
//   Sparkles,
//   Globe,
//   Plus,
//   Trash2,
//   Target,
//   Loader2,
//   Wand2,
//   Edit3,
//   AlertCircle,
//   CheckCircle2,
//   FileText,
//   Bot,
// } from "lucide-react";
// import { toast } from "sonner";

// import { http } from "@/services/apiClient";
// import { Button } from "@/components/ui/button";
// import {
//   Card,
//   CardHeader,
//   CardTitle,
//   CardDescription,
//   CardContent,
// } from "@/components/ui/card";
// import { Input } from "@/components/ui/input";
// import { Label } from "@/components/ui/label";
// import { Textarea } from "@/components/ui/textarea";
// import { Badge } from "@/components/ui/badge";
// import { Separator } from "@/components/ui/separator";
// import { PageHeader } from "@/components/ui/page-header";
// import { Switch } from "@/components/ui/switch";
// import {
//   Tabs,
//   TabsList,
//   TabsTrigger,
//   TabsContent,
// } from "@/components/ui/tabs";
// import {
//   Dialog,
//   DialogContent,
//   DialogDescription,
//   DialogFooter,
//   DialogHeader,
//   DialogTitle,
//   DialogTrigger,
// } from "@/components/ui/dialog";
// import {
//   Tooltip,
//   TooltipContent,
//   TooltipTrigger,
// } from "@/components/ui/tooltip";

// /* ── Types (aligned with backend IcpResponse schema) ───────────────── */

// interface IcpProfile {
//   id: string;
//   name: string;
//   persona: string | null;
//   companyType: string | null;
//   topObjections: string[];
//   painPoints: string[];
//   valueProps: string[];
//   senderRole: string | null;
//   senderCompany: string | null;
//   senderOffer: string | null;
//   proofMetric: string | null;
//   createdAt: string;
//   updatedAt: string;
// }

// interface LlmConfig {
//   id: string;
//   isActive?: boolean;
//   is_active?: boolean;
//   isDefault?: boolean;
//   is_default?: boolean;
// }

// /* Flat form state — 3 indexed inputs per array field */
// interface IcpFormState {
//   name: string;
//   persona: string;
//   companyType: string;
//   senderRole: string;
//   senderCompany: string;
//   senderOffer: string;
//   proofMetric: string;
//   objection1: string;
//   objection2: string;
//   objection3: string;
//   pain1: string;
//   pain2: string;
//   pain3: string;
//   value1: string;
//   value2: string;
//   value3: string;
// }

// const EMPTY_FORM: IcpFormState = {
//   name: "",
//   persona: "",
//   companyType: "",
//   senderRole: "",
//   senderCompany: "",
//   senderOffer: "",
//   proofMetric: "",
//   objection1: "",
//   objection2: "",
//   objection3: "",
//   pain1: "",
//   pain2: "",
//   pain3: "",
//   value1: "",
//   value2: "",
//   value3: "",
// };

// /* ── Helpers ────────────────────────────────────────────────────────── */

// function profileToForm(p: IcpProfile): IcpFormState {
//   return {
//     name: p.name,
//     persona: p.persona ?? "",
//     companyType: p.companyType ?? "",
//     senderRole: p.senderRole ?? "",
//     senderCompany: p.senderCompany ?? "",
//     senderOffer: p.senderOffer ?? "",
//     proofMetric: p.proofMetric ?? "",
//     objection1: p.topObjections[0] ?? "",
//     objection2: p.topObjections[1] ?? "",
//     objection3: p.topObjections[2] ?? "",
//     pain1: p.painPoints[0] ?? "",
//     pain2: p.painPoints[1] ?? "",
//     pain3: p.painPoints[2] ?? "",
//     value1: p.valueProps[0] ?? "",
//     value2: p.valueProps[1] ?? "",
//     value3: p.valueProps[2] ?? "",
//   };
// }

// function formToPayload(f: IcpFormState) {
//   return {
//     name: f.name.trim(),
//     persona: f.persona.trim() || null,
//     companyType: f.companyType.trim() || null,
//     senderRole: f.senderRole.trim() || null,
//     senderCompany: f.senderCompany.trim() || null,
//     senderOffer: f.senderOffer.trim() || null,
//     proofMetric: f.proofMetric.trim() || null,
//     topObjections: [f.objection1, f.objection2, f.objection3].filter(Boolean),
//     painPoints: [f.pain1, f.pain2, f.pain3].filter(Boolean),
//     valueProps: [f.value1, f.value2, f.value3].filter(Boolean),
//   };
// }

// function normaliseProfiles(raw: unknown): IcpProfile[] {
//   if (Array.isArray(raw)) return raw as IcpProfile[];
//   if (raw && typeof raw === "object" && "items" in raw)
//     return (raw as { items: IcpProfile[] }).items ?? [];
//   return [];
// }

// /* ── Page ──────────────────────────────────────────────────────────── */

// export function IcpProfilesPage() {
//   const qc = useQueryClient();

//   /* ── Dialog state ── */
//   const [crudOpen, setCrudOpen] = useState(false);
//   const [editingId, setEditingId] = useState<string | null>(null);
//   const [form, setForm] = useState<IcpFormState>(EMPTY_FORM);

//   const [discoverOpen, setDiscoverOpen] = useState(false);
//   const [discoverMode, setDiscoverMode] = useState<"website" | "description">(
//     "website"
//   );
//   const [discoverWebsite, setDiscoverWebsite] = useState("");
//   const [discoverDescription, setDiscoverDescription] = useState("");
//   const [discoverSave, setDiscoverSave] = useState(true);
//   const [discoverResult, setDiscoverResult] = useState<unknown>(null);
//   const [discovering, setDiscovering] = useState(false);

//   const [suggesting, setSuggesting] = useState(false);

//   const [deleteTarget, setDeleteTarget] = useState<IcpProfile | null>(null);

//   /* ── Queries ── */
//   const profilesQ = useQuery<IcpProfile[]>({
//     queryKey: ["icp-profiles"],
//     queryFn: () =>
//       http.get<unknown>("/api/v1/icp-profiles").then(normaliseProfiles),
//   });
//   const profiles = profilesQ.data ?? [];

//   const llmQ = useQuery<LlmConfig[]>({
//     queryKey: ["llm-configs"],
//     queryFn: () =>
//       http
//         .get<unknown>("/api/v1/llm-configs")
//         .then((r) => (Array.isArray(r) ? r : (r as { items?: LlmConfig[] })?.items ?? [])),
//   });
//   const hasLlm = (llmQ.data ?? []).some(
//     (c) => (c.isActive ?? c.is_active) !== false
//   );

//   /* ── Mutations ── */
//   const saveMutation = useMutation({
//     mutationFn: (payload: ReturnType<typeof formToPayload> & { id?: string }) => {
//       const { id, ...body } = payload;
//       if (id) return http.put<IcpProfile>(`/api/v1/icp-profiles/${id}`, body);
//       return http.post<IcpProfile>("/api/v1/icp-profiles", body);
//     },
//     onSuccess: () => {
//       toast.success(editingId ? "ICP profile updated" : "ICP profile saved");
//       setCrudOpen(false);
//       setForm(EMPTY_FORM);
//       setEditingId(null);
//       qc.invalidateQueries({ queryKey: ["icp-profiles"] });
//     },
//     onError: () => toast.error("Failed to save ICP profile"),
//   });

//   const deleteMutation = useMutation({
//     mutationFn: (id: string) => http.delete(`/api/v1/icp-profiles/${id}`),
//     onSuccess: () => {
//       toast.success("ICP profile deleted");
//       setDeleteTarget(null);
//       qc.invalidateQueries({ queryKey: ["icp-profiles"] });
//     },
//     onError: () => toast.error("Delete failed"),
//   });

//   /* ── Handlers ── */
//   function openCreate() {
//     setEditingId(null);
//     setForm(EMPTY_FORM);
//     setCrudOpen(true);
//   }

//   function openEdit(p: IcpProfile) {
//     setEditingId(p.id);
//     setForm(profileToForm(p));
//     setCrudOpen(true);
//   }

//   function handleSave() {
//     if (!form.name.trim()) {
//       toast.error("Profile name is required");
//       return;
//     }
//     const payload = formToPayload(form);
//     saveMutation.mutate(editingId ? { ...payload, id: editingId } : payload);
//   }

//   async function handleAiSuggest() {
//     if (!form.persona.trim()) {
//       toast.error("Enter a persona description first, then click AI Suggest");
//       return;
//     }
//     setSuggesting(true);
//     try {
//       const data = await http.post<{
//         suggestions?: {
//           painPoints?: string[];
//           objections?: string[];
//           valueProps?: string[];
//           proofMetric?: string;
//         };
//         painPoints?: string[];
//         topObjections?: string[];
//         valueProps?: string[];
//         proofMetric?: string;
//       }>("/api/v1/icp-profiles/suggest", {
//         seed: form.persona,
//       });
//       // Support both wrapped (data.suggestions) and flat response shapes
//       const s = (data as { suggestions?: typeof data }).suggestions ?? data;
//       const pains = s.painPoints ?? [];
//       const objections = s.topObjections ?? (s as { objections?: string[] }).objections ?? [];
//       const values = s.valueProps ?? [];
//       setForm((f) => ({
//         ...f,
//         pain1: pains[0] ?? f.pain1,
//         pain2: pains[1] ?? f.pain2,
//         pain3: pains[2] ?? f.pain3,
//         objection1: objections[0] ?? f.objection1,
//         objection2: objections[1] ?? f.objection2,
//         objection3: objections[2] ?? f.objection3,
//         value1: values[0] ?? f.value1,
//         value2: values[1] ?? f.value2,
//         value3: values[2] ?? f.value3,
//         proofMetric: (s.proofMetric ?? f.proofMetric) || f.proofMetric,
//       }));
//       toast.success("AI suggestions applied! Review and adjust as needed.");
//     } catch {
//       toast.error("AI suggestion failed");
//     }
//     setSuggesting(false);
//   }

//   function resetDiscover() {
//     setDiscoverMode("website");
//     setDiscoverWebsite("");
//     setDiscoverDescription("");
//     setDiscoverSave(true);
//     setDiscoverResult(null);
//   }

//   async function handleAutoDiscover() {
//     if (discoverMode === "website" && !discoverWebsite.trim()) {
//       toast.error("Enter your website URL");
//       return;
//     }
//     if (discoverMode === "description" && discoverDescription.trim().length < 10) {
//       toast.error("Enter at least 10 characters describing your ICP");
//       return;
//     }
//     setDiscovering(true);
//     setDiscoverResult(null);
//     try {
//       // Backend /auto-discover accepts a list of prospects. For the
//       // website/description flow we send a synthetic single-prospect payload
//       // with the input embedded in the description field.
//       const syntheticProspect =
//         discoverMode === "website"
//           ? { description: `website: ${discoverWebsite.trim()}` }
//           : { description: discoverDescription.trim() };

//       const data = await http.post<unknown>(
//         "/api/v1/icp-profiles/auto-discover",
//         {
//           prospects: [syntheticProspect],
//           existingIcpId: null,
//         }
//       );
//       setDiscoverResult(data);

//       if (discoverSave) {
//         // Auto-save: derive a profile from the response and POST it
//         const r = data as {
//           suggestedPersona?: string;
//           commonAttributes?: Record<string, unknown>;
//         };
//         const name =
//           discoverMode === "website"
//             ? `Profile from ${discoverWebsite.trim()}`
//             : "Auto-Discovered ICP";
//         await http.post<IcpProfile>("/api/v1/icp-profiles", {
//           name,
//           persona: r.suggestedPersona ?? "",
//           companyType:
//             String(r.commonAttributes?.companyType ?? "") || null,
//           painPoints: [],
//           topObjections: [],
//           valueProps: [],
//         });
//         toast.success(`Auto-discovered ICP saved — "${name}"`);
//         qc.invalidateQueries({ queryKey: ["icp-profiles"] });
//         setTimeout(() => {
//           setDiscoverOpen(false);
//           resetDiscover();
//         }, 2500);
//       } else {
//         toast.success("Auto-discovery complete — review the result below");
//       }
//     } catch {
//       toast.error("Auto-discovery failed");
//     }
//     setDiscovering(false);
//   }

//   async function handleSaveDiscoveredProfile(profileData: {
//     name?: string;
//     persona?: string;
//     companyType?: string | null;
//   }) {
//     try {
//       await http.post<IcpProfile>("/api/v1/icp-profiles", {
//         name: profileData.name ?? "Auto-Discovered ICP",
//         persona: profileData.persona ?? "",
//         companyType: profileData.companyType ?? null,
//         painPoints: [],
//         topObjections: [],
//         valueProps: [],
//       });
//       toast.success(`Saved "${profileData.name ?? "ICP"}" to your list`);
//       qc.invalidateQueries({ queryKey: ["icp-profiles"] });
//       setDiscoverOpen(false);
//       resetDiscover();
//     } catch {
//       toast.error("Failed to save profile");
//     }
//   }

//   /* ── Render ── */
//   return (
//     <div className="space-y-6 p-6">
//       <PageHeader
//         title="ICP Profiles"
//         description="Define your Ideal Customer Profiles for targeted outreach"
//         actions={
//           <div className="flex items-center gap-2">
//             {/* Auto-Discover ICP */}
//             <Dialog
//               open={discoverOpen}
//               onOpenChange={(o) => {
//                 setDiscoverOpen(o);
//                 if (!o) resetDiscover();
//               }}
//             >
//               <DialogTrigger asChild>
//                 <Button
//                   variant="outline"
//                   className="border-violet-300 text-violet-700 hover:bg-violet-50"
//                   onClick={() => {
//                     resetDiscover();
//                     setDiscoverOpen(true);
//                   }}
//                 >
//                   <Wand2 className="h-4 w-4 mr-2" />
//                   Auto-Discover ICP
//                 </Button>
//               </DialogTrigger>
//               <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
//                 <DialogHeader>
//                   <DialogTitle>Auto-Discover ICP</DialogTitle>
//                   <DialogDescription>
//                     Let the AI infer your Ideal Customer Profile — the fastest
//                     way to get started.
//                   </DialogDescription>
//                 </DialogHeader>

//                 <div className="space-y-4 py-4">
//                   {/* LLM warning */}
//                   {!hasLlm && (
//                     <div className="rounded-md bg-amber-50 border border-amber-300 p-4 text-sm space-y-2">
//                       <p className="font-medium text-amber-900 flex items-center gap-2">
//                         <AlertCircle className="h-4 w-4" />
//                         No AI model configured
//                       </p>
//                       <p className="text-amber-800 text-xs">
//                         Auto-Discover needs an LLM. Configure one in LLM
//                         Models first.
//                       </p>
//                     </div>
//                   )}

//                   {/* Mode tabs */}
//                   <Tabs
//                     value={discoverMode}
//                     onValueChange={(v) => {
//                       setDiscoverMode(v as "website" | "description");
//                       setDiscoverResult(null);
//                     }}
//                   >
//                     <TabsList className="grid w-full grid-cols-2">
//                       <TabsTrigger value="website">
//                         <Globe className="h-3.5 w-3.5 mr-1.5" />
//                         From your website
//                       </TabsTrigger>
//                       <TabsTrigger value="description">
//                         <FileText className="h-3.5 w-3.5 mr-1.5" />
//                         From description
//                       </TabsTrigger>
//                     </TabsList>

//                     <TabsContent value="website" className="space-y-3 mt-4">
//                       <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800">
//                         <p className="font-medium mb-1">How it works</p>
//                         <p>
//                           Enter your company URL. The AI reads your website,
//                           searches for competitors and industry context, and
//                           infers your ICP automatically — persona, company
//                           type, pain points, and value propositions.
//                         </p>
//                       </div>
//                       <div className="space-y-2">
//                         <Label>Your Website URL</Label>
//                         <Input
//                           placeholder="e.g. mycompany.com"
//                           value={discoverWebsite}
//                           onChange={(e) => setDiscoverWebsite(e.target.value)}
//                         />
//                         <p className="text-xs text-muted-foreground">
//                           No need to include https:// — we'll strip it.
//                         </p>
//                       </div>
//                     </TabsContent>

//                     <TabsContent
//                       value="description"
//                       className="space-y-3 mt-4"
//                     >
//                       <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800">
//                         <p className="font-medium mb-1">How it works</p>
//                         <p>
//                           Describe your ideal customer in plain English. The
//                           AI structures it into a full ICP profile — persona,
//                           company type, pain points, objections, and value
//                           propositions.
//                         </p>
//                       </div>
//                       <div className="space-y-2">
//                         <Label>Describe your ideal customer</Label>
//                         <Textarea
//                           placeholder="e.g. We sell to VP of Engineering at Series B SaaS companies (50–500 employees) who are struggling with slow release cycles..."
//                           value={discoverDescription}
//                           onChange={(e) =>
//                             setDiscoverDescription(e.target.value)
//                           }
//                           rows={5}
//                         />
//                         <p className="text-xs text-muted-foreground">
//                           The more specific you are, the better the AI result.
//                         </p>
//                       </div>
//                     </TabsContent>
//                   </Tabs>

//                   <Separator />

//                   {/* Save toggle */}
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <p className="text-sm font-medium">
//                         Save profiles automatically
//                       </p>
//                       <p className="text-xs text-muted-foreground">
//                         If on, discovered profiles are saved immediately. If
//                         off, you can review first.
//                       </p>
//                     </div>
//                     <Switch
//                       checked={discoverSave}
//                       onCheckedChange={setDiscoverSave}
//                     />
//                   </div>

//                   {/* Result */}
//                   {Boolean(discoverResult) && (
//                     <DiscoverResult
//                       result={discoverResult as DiscoverResultShape}
//                       discoverMode={discoverMode}
//                       discoverSave={discoverSave}
//                       discoverWebsite={discoverWebsite}
//                       onSaveProfile={handleSaveDiscoveredProfile}
//                     />
//                   )}
//                 </div>

//                 <DialogFooter>
//                   {discovering && (
//                     <p className="text-xs text-muted-foreground mr-auto flex items-center gap-1.5">
//                       <Loader2 className="h-3 w-3 animate-spin" />
//                       {discoverMode === "website"
//                         ? "Searching the web + AI analysis (30–60 s)…"
//                         : "AI analyzing your description (10–20 s)…"}
//                     </p>
//                   )}
//                   <Button
//                     variant="outline"
//                     onClick={() => {
//                       setDiscoverOpen(false);
//                       resetDiscover();
//                     }}
//                   >
//                     Close
//                   </Button>
//                   <Button
//                     onClick={handleAutoDiscover}
//                     disabled={discovering || !hasLlm}
//                   >
//                     {discovering ? (
//                       <>
//                         <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
//                         Discovering…
//                       </>
//                     ) : (
//                       <>
//                         <Wand2 className="h-3.5 w-3.5 mr-1.5" />
//                         Run Auto-Discovery
//                       </>
//                     )}
//                   </Button>
//                 </DialogFooter>
//               </DialogContent>
//             </Dialog>

//             {/* Create ICP (manual) */}
//             <Dialog
//               open={crudOpen}
//               onOpenChange={(o) => {
//                 setCrudOpen(o);
//                 if (!o) {
//                   setForm(EMPTY_FORM);
//                   setEditingId(null);
//                 }
//               }}
//             >
//               <DialogTrigger asChild>
//                 <Button onClick={openCreate}>
//                   <Plus className="h-4 w-4 mr-2" />
//                   Create ICP
//                 </Button>
//               </DialogTrigger>
//               <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
//                 <IcpFormDialog
//                   form={form}
//                   setForm={setForm}
//                   editingId={editingId}
//                   suggesting={suggesting}
//                   saving={saveMutation.isPending}
//                   hasLlm={hasLlm}
//                   onAiSuggest={handleAiSuggest}
//                   onSave={handleSave}
//                   onClose={() => {
//                     setCrudOpen(false);
//                     setForm(EMPTY_FORM);
//                     setEditingId(null);
//                   }}
//                 />
//               </DialogContent>
//             </Dialog>
//           </div>
//         }
//       />

//       {/* LLM warning banner */}
//       {!hasLlm && !llmQ.isLoading && (
//         <div className="rounded-md bg-amber-50 border border-amber-300 p-4 flex items-start gap-3">
//           <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
//           <div className="space-y-1">
//             <p className="text-sm font-medium text-amber-900">
//               No AI model configured
//             </p>
//             <p className="text-xs text-amber-800">
//               AI Suggest and Auto-Discover require an active LLM. Configure
//               one in{" "}
//               <span className="font-semibold">Setup → LLM Models</span>.
//             </p>
//           </div>
//           <Bot className="h-5 w-5 text-amber-500 ml-auto shrink-0" />
//         </div>
//       )}

//       {/* Profile grid */}
//       {profilesQ.isLoading ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
//           {[0, 1, 2].map((i) => (
//             <div
//               key={i}
//               className="h-48 animate-pulse rounded-lg bg-muted"
//             />
//           ))}
//         </div>
//       ) : profilesQ.isError ? (
//         <Card>
//           <CardContent className="py-12 text-center">
//             <p className="text-muted-foreground">
//               Failed to load ICP profiles.
//             </p>
//             <Button
//               onClick={() => profilesQ.refetch()}
//               className="mt-4"
//             >
//               Retry
//             </Button>
//           </CardContent>
//         </Card>
//       ) : profiles.length === 0 ? (
//         <Card>
//           <CardContent className="py-12 text-center">
//             <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
//             <h4 className="font-medium mb-2">No ICP Profiles Yet</h4>
//             <p className="text-sm text-muted-foreground mb-4">
//               Create your first ICP to start targeting the right prospects.
//             </p>
//             <Button size="sm" onClick={openCreate}>
//               <Plus className="h-4 w-4 mr-1" />
//               Create ICP
//             </Button>
//           </CardContent>
//         </Card>
//       ) : (
//         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
//           {profiles.map((p) => (
//             <IcpCard
//               key={p.id}
//               profile={p}
//               onEdit={() => openEdit(p)}
//               onDelete={() => setDeleteTarget(p)}
//             />
//           ))}
//         </div>
//       )}

//       {/* Delete confirmation dialog */}
//       <Dialog
//         open={!!deleteTarget}
//         onOpenChange={(o) => !o && setDeleteTarget(null)}
//       >
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Delete ICP profile?</DialogTitle>
//             <DialogDescription>
//               {deleteTarget?.name
//                 ? `"${deleteTarget.name}" will be permanently removed. Prospects linked to this profile will lose their ICP scoring. This action cannot be undone.`
//                 : "This ICP profile will be permanently removed. This action cannot be undone."}
//             </DialogDescription>
//           </DialogHeader>
//           <DialogFooter>
//             <Button
//               variant="outline"
//               onClick={() => setDeleteTarget(null)}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="destructive"
//               onClick={() =>
//                 deleteTarget && deleteMutation.mutate(deleteTarget.id)
//               }
//               disabled={deleteMutation.isPending}
//             >
//               {deleteMutation.isPending ? "Deleting…" : "Delete"}
//             </Button>
//           </DialogFooter>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// }

// /* ── IcpCard ────────────────────────────────────────────────────────── */

// function IcpCard({
//   profile: p,
//   onEdit,
//   onDelete,
// }: {
//   profile: IcpProfile;
//   onEdit: () => void;
//   onDelete: () => void;
// }) {
//   return (
//     <Card>
//       <CardHeader className="pb-2">
//         <div className="flex items-center justify-between">
//           <CardTitle className="text-sm truncate">{p.name}</CardTitle>
//           <div className="flex items-center gap-1 shrink-0">
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <Button
//                   size="icon"
//                   variant="ghost"
//                   className="h-8 w-8"
//                   onClick={onEdit}
//                   aria-label="Edit"
//                 >
//                   <Edit3 className="h-3.5 w-3.5" />
//                 </Button>
//               </TooltipTrigger>
//               <TooltipContent>Edit this ICP profile</TooltipContent>
//             </Tooltip>
//             <Tooltip>
//               <TooltipTrigger asChild>
//                 <Button
//                   size="icon"
//                   variant="ghost"
//                   className="h-8 w-8 text-red-500 hover:text-red-700"
//                   onClick={onDelete}
//                   aria-label="Delete"
//                 >
//                   <Trash2 className="h-3.5 w-3.5" />
//                 </Button>
//               </TooltipTrigger>
//               <TooltipContent>Delete this ICP profile</TooltipContent>
//             </Tooltip>
//           </div>
//         </div>
//         <CardDescription className="text-xs">
//           {p.companyType ?? "No company type"}
//         </CardDescription>
//       </CardHeader>
//       <CardContent className="space-y-2 text-xs">
//         {p.persona && (
//           <p className="text-muted-foreground line-clamp-2">{p.persona}</p>
//         )}
//         {p.topObjections.length > 0 && (
//           <div>
//             <p className="font-medium mb-1">Objections:</p>
//             <div className="flex flex-wrap gap-1">
//               {p.topObjections.map((o, i) => (
//                 <Badge key={i} variant="secondary" className="text-xs">
//                   {o}
//                 </Badge>
//               ))}
//             </div>
//           </div>
//         )}
//         {p.painPoints.length > 0 && (
//           <div>
//             <p className="font-medium mb-1">Pain Points:</p>
//             <div className="flex flex-wrap gap-1">
//               {p.painPoints.map((pn, i) => (
//                 <Badge key={i} variant="outline" className="text-xs">
//                   {pn}
//                 </Badge>
//               ))}
//             </div>
//           </div>
//         )}
//         {p.valueProps.length > 0 && (
//           <div>
//             <p className="font-medium mb-1">Value Props:</p>
//             <div className="flex flex-wrap gap-1">
//               {p.valueProps.map((v, i) => (
//                 <Badge
//                   key={i}
//                   className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200"
//                 >
//                   {v}
//                 </Badge>
//               ))}
//             </div>
//           </div>
//         )}
//         {(p.senderCompany ?? p.senderRole) && (
//           <p className="text-muted-foreground mt-1">
//             From:{" "}
//             {[p.senderRole, p.senderCompany].filter(Boolean).join(" at ")}
//           </p>
//         )}
//         {p.proofMetric && (
//           <p className="text-muted-foreground">Proof: {p.proofMetric}</p>
//         )}
//       </CardContent>
//     </Card>
//   );
// }

// /* ── IcpFormDialog ──────────────────────────────────────────────────── */

// function IcpFormDialog({
//   form,
//   setForm,
//   editingId,
//   suggesting,
//   saving,
//   hasLlm,
//   onAiSuggest,
//   onSave,
//   onClose,
// }: {
//   form: IcpFormState;
//   setForm: React.Dispatch<React.SetStateAction<IcpFormState>>;
//   editingId: string | null;
//   suggesting: boolean;
//   saving: boolean;
//   hasLlm: boolean;
//   onAiSuggest: () => void;
//   onSave: () => void;
//   onClose: () => void;
// }) {
//   function f(key: keyof IcpFormState) {
//     return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
//       setForm((prev) => ({ ...prev, [key]: e.target.value }));
//   }

//   return (
//     <>
//       <DialogHeader>
//         <DialogTitle>
//           {editingId ? "Edit ICP Profile" : "Create ICP Profile"}
//         </DialogTitle>
//         <DialogDescription>
//           {editingId
//             ? "Update your target buyer persona"
//             : "Define your target buyer persona"}
//         </DialogDescription>
//       </DialogHeader>

//       <div className="space-y-4 py-4">
//         {/* Core fields */}
//         <div className="grid grid-cols-2 gap-4">
//           <div className="space-y-2">
//             <Label>Profile Name *</Label>
//             <Input
//               placeholder="e.g. SaaS CTOs under 200"
//               value={form.name}
//               onChange={f("name")}
//             />
//           </div>
//           <div className="space-y-2">
//             <Label>Company Type</Label>
//             <Input
//               placeholder="e.g. B2B SaaS, Series A–C"
//               value={form.companyType}
//               onChange={f("companyType")}
//             />
//           </div>
//         </div>

//         <div className="space-y-2">
//           <Label>Target Persona</Label>
//           <Textarea
//             placeholder="Describe your ideal buyer in detail…"
//             value={form.persona}
//             onChange={f("persona")}
//             rows={3}
//           />
//         </div>

//         <Separator />
//         <p className="text-sm font-medium">Sender Information</p>
//         <div className="grid grid-cols-2 gap-4">
//           <div className="space-y-2">
//             <Label>Your Role</Label>
//             <Input
//               placeholder="e.g. CEO at MyCo"
//               value={form.senderRole}
//               onChange={f("senderRole")}
//             />
//           </div>
//           <div className="space-y-2">
//             <Label>Your Company</Label>
//             <Input
//               placeholder="e.g. MyCo"
//               value={form.senderCompany}
//               onChange={f("senderCompany")}
//             />
//           </div>
//           <div className="space-y-2">
//             <Label>Your Offer</Label>
//             <Input
//               placeholder="e.g. AI lead gen platform"
//               value={form.senderOffer}
//               onChange={f("senderOffer")}
//             />
//           </div>
//           <div className="space-y-2">
//             <Label>Proof Metric</Label>
//             <Input
//               placeholder="e.g. 3× pipeline in 90 days"
//               value={form.proofMetric}
//               onChange={f("proofMetric")}
//             />
//           </div>
//         </div>

//         <Separator />
//         <p className="text-sm font-medium">Top 3 Objections</p>
//         <p className="text-xs text-muted-foreground">
//           What will this buyer push back on when approached?
//         </p>
//         <div className="grid grid-cols-1 gap-2">
//           <Input
//             placeholder='e.g. "We already have a solution for this"'
//             value={form.objection1}
//             onChange={f("objection1")}
//           />
//           <Input
//             placeholder="e.g. &quot;We don't have budget for new tools right now&quot;"
//             value={form.objection2}
//             onChange={f("objection2")}
//           />
//           <Input
//             placeholder='e.g. "Our team is too busy to evaluate another vendor"'
//             value={form.objection3}
//             onChange={f("objection3")}
//           />
//         </div>

//         <Separator />
//         <p className="text-sm font-medium">Pain Points</p>
//         <p className="text-xs text-muted-foreground">
//           What daily frustrations or strategic gaps does this buyer face?
//         </p>
//         <div className="grid grid-cols-1 gap-2">
//           <Input
//             placeholder='e.g. "Manual prospecting takes 20+ hours per week"'
//             value={form.pain1}
//             onChange={f("pain1")}
//           />
//           <Input
//             placeholder='e.g. "Low reply rates on outbound campaigns (under 2%)"'
//             value={form.pain2}
//             onChange={f("pain2")}
//           />
//           <Input
//             placeholder='e.g. "Sales reps send generic templates that damage brand"'
//             value={form.pain3}
//             onChange={f("pain3")}
//           />
//         </div>

//         <Separator />
//         <p className="text-sm font-medium">Value Propositions</p>
//         <p className="text-xs text-muted-foreground">
//           What specific outcomes can you deliver? Use metrics when possible.
//         </p>
//         <div className="grid grid-cols-1 gap-2">
//           <Input
//             placeholder='e.g. "3× more qualified pipeline in 90 days"'
//             value={form.value1}
//             onChange={f("value1")}
//           />
//           <Input
//             placeholder='e.g. "Cut prospecting time from 20 hrs to 2 hrs per week"'
//             value={form.value2}
//             onChange={f("value2")}
//           />
//           <Input
//             placeholder='e.g. "AI-written emails that sound human, not like a vendor"'
//             value={form.value3}
//             onChange={f("value3")}
//           />
//         </div>
//       </div>

//       <DialogFooter>
//         <Button variant="outline" onClick={onClose}>
//           Cancel
//         </Button>
//         <Button
//           variant="outline"
//           className="border-violet-300 text-violet-700 hover:bg-violet-50"
//           onClick={onAiSuggest}
//           disabled={suggesting || !form.persona.trim() || !hasLlm}
//         >
//           <Sparkles className="h-3.5 w-3.5 mr-1.5" />
//           {suggesting ? "Suggesting…" : "AI Suggest Fields"}
//         </Button>
//         <Button onClick={onSave} disabled={saving}>
//           {saving ? (
//             <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
//           ) : null}
//           {editingId ? "Update ICP" : "Save ICP"}
//         </Button>
//       </DialogFooter>
//     </>
//   );
// }

// /* ── DiscoverResult ─────────────────────────────────────────────────── */

// interface DiscoverResultShape {
//   suggestedPersona?: string;
//   commonAttributes?: Record<string, unknown>;
// }

// function DiscoverResult({
//   result,
//   discoverMode,
//   discoverSave,
//   discoverWebsite,
//   onSaveProfile,
// }: {
//   result: DiscoverResultShape;
//   discoverMode: "website" | "description";
//   discoverSave: boolean;
//   discoverWebsite: string;
//   onSaveProfile: (p: {
//     name?: string;
//     persona?: string;
//     companyType?: string | null;
//   }) => void;
// }) {
//   return (
//     <div className="space-y-3 rounded-md border p-4 bg-muted/30">
//       <div className="flex items-center justify-between">
//         <p className="text-sm font-medium flex items-center gap-2">
//           <CheckCircle2 className="h-4 w-4 text-emerald-600" />
//           Discovery Result
//         </p>
//         <Badge variant="outline" className="text-xs font-mono">
//           {discoverMode === "website" ? (
//             <Globe className="h-3 w-3 mr-1" />
//           ) : (
//             <FileText className="h-3 w-3 mr-1" />
//           )}
//           {discoverMode === "website" ? discoverWebsite : "your description"}
//         </Badge>
//       </div>

//       {/* Suggested persona */}
//       {result.suggestedPersona && (
//         <div className="text-xs space-y-1">
//           <p>
//             <span className="font-medium">Suggested Persona:</span>{" "}
//             {`${result.suggestedPersona}`}
//           </p>
//         </div>
//       )}

//       {/* Common attributes */}
//       {result.commonAttributes && Object.keys(result.commonAttributes).length > 0 && (
//           <div className="text-xs space-y-1">
//             <p className="font-medium">Common Attributes:</p>
//             {Object.entries(result.commonAttributes).map(
//               ([k, v]) => (
//                 <p key={k}>
//                   <span className="font-medium capitalize">{k}:</span>{" "}
//                   {`${v}`}
//                 </p>
//               )
//             )}
//           </div>
//         )}

//       {/* Review mode save button */}
//       {!discoverSave && (
//         <Button
//           size="sm"
//           className="mt-2"
//           onClick={() =>
//             onSaveProfile({
//               name:
//                 discoverMode === "website"
//                   ? `Profile from ${discoverWebsite}`
//                   : "Auto-Discovered ICP",
//               persona: result.suggestedPersona
//                 ? String(result.suggestedPersona)
//                 : undefined,
//               companyType:
//                 result.commonAttributes && "companyType" in result.commonAttributes
//                   ? `${result.commonAttributes.companyType}`
//                   : null,
//             })
//           }
//         >
//           <Plus className="h-3.5 w-3.5 mr-1.5" />
//           Save {"&"} Close
//         </Button>
//       )}

//       {discoverSave && (
//         <p className="text-xs text-emerald-700 font-medium">
//           ✓ Profile saved — modal will close shortly.
//         </p>
//       )}
//     </div>
//   );
// }

/**
 * IcpProfilesPage.tsx — ICP profiles CRUD + AI Suggest + Auto-Discover.
 *
 * Layout  : card grid (3-col responsive), matching the Next.js reference.
 * API     : /api/v1/icp-profiles (list, create, update, delete)
 *           /api/v1/icp-profiles/suggest  (AI suggest fields)
 *           /api/v1/icp-profiles/auto-discover (derive ICP from prospects)
 *
 * ICP-1  ✓ Auto-Discover dialog — 2-tab (website / description), save toggle,
 *            result preview, per-persona save.
 * ICP-2  ✓ Correct form fields: name, persona, companyType, topObjections (3),
 *            painPoints (3), valueProps (3), senderRole, senderCompany,
 *            senderOffer, proofMetric.
 * ICP-3  ✓ Card grid display — all fields shown with badge truncation + expand.
 * ICP-4  ✓ Edit dialog pre-populated with existing values.
 * ICP-5  ✓ Delete with confirmation dialog.
 * ICP-6  ✓ LLM config warning banner when no active LLM.
 */
import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  Globe,
  Plus,
  Trash2,
  Target,
  Loader2,
  Wand2,
  Edit3,
  AlertCircle,
  CheckCircle2,
  FileText,
  Bot,
  Users,
  Database,
  Search,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { http, datamoonApi } from "@/services/apiClient";
import { DataMoonSourcePanel } from "@/features/datamoon/DataMoonSourcePanel";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/ui/page-header";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/* ── Types (aligned with backend IcpResponse schema) ───────────────── */

interface IcpProfile {
  id: string;
  name: string;
  persona: string | null;
  companyType: string | null;
  topObjections: string[];
  painPoints: string[];
  valueProps: string[];
  senderRole: string | null;
  senderCompany: string | null;
  senderOffer: string | null;
  proofMetric: string | null;
  prospect_mode: string;
  datamoon_filters: Array<{ field: string; operator: string; value: unknown }> | null;
  datamoon_limit: number | null;
  createdAt: string;
  updatedAt: string;
  /** Populated by the list endpoint — count of prospects linked to this ICP. */
  prospectCount?: number | null;
}

interface LlmConfig {
  id: string;
  isActive?: boolean;
  is_active?: boolean;
  isDefault?: boolean;
  is_default?: boolean;
}

/* Flat form state — 3 indexed inputs per array field */
interface IcpFormState {
  name: string;
  persona: string;
  companyType: string;
  senderRole: string;
  senderCompany: string;
  senderOffer: string;
  proofMetric: string;
  objection1: string;
  objection2: string;
  objection3: string;
  pain1: string;
  pain2: string;
  pain3: string;
  value1: string;
  value2: string;
  value3: string;
  /** 'manual' | 'datamoon' | 'web_scraping' | 'linkedin' */
  prospectMode: string;
  datamoonFilters: Array<{ field: string; operator: string; value: unknown }> | null;
  datamoonLimit: string;
}

const EMPTY_FORM: IcpFormState = {
  name: "",
  persona: "",
  companyType: "",
  senderRole: "",
  senderCompany: "",
  senderOffer: "",
  proofMetric: "",
  objection1: "",
  objection2: "",
  objection3: "",
  pain1: "",
  pain2: "",
  pain3: "",
  value1: "",
  value2: "",
  value3: "",
  prospectMode: "manual",
  datamoonFilters: null,
  datamoonLimit: "",
};

/* ── Helpers ────────────────────────────────────────────────────────── */

function profileToForm(p: IcpProfile): IcpFormState {
  return {
    name: p.name,
    persona: p.persona ?? "",
    companyType: p.companyType ?? "",
    senderRole: p.senderRole ?? "",
    senderCompany: p.senderCompany ?? "",
    senderOffer: p.senderOffer ?? "",
    proofMetric: p.proofMetric ?? "",
    objection1: p.topObjections[0] ?? "",
    objection2: p.topObjections[1] ?? "",
    objection3: p.topObjections[2] ?? "",
    pain1: p.painPoints[0] ?? "",
    pain2: p.painPoints[1] ?? "",
    pain3: p.painPoints[2] ?? "",
    value1: p.valueProps[0] ?? "",
    value2: p.valueProps[1] ?? "",
    value3: p.valueProps[2] ?? "",
    prospectMode: p.prospect_mode ?? "manual",
    datamoonFilters: p.datamoon_filters ?? null,
    datamoonLimit: p.datamoon_limit != null ? String(p.datamoon_limit) : "",
  };
}

function formToPayload(f: IcpFormState) {
  return {
    name: f.name.trim(),
    persona: f.persona.trim() || null,
    companyType: f.companyType.trim() || null,
    senderRole: f.senderRole.trim() || null,
    senderCompany: f.senderCompany.trim() || null,
    senderOffer: f.senderOffer.trim() || null,
    proofMetric: f.proofMetric.trim() || null,
    topObjections: [f.objection1, f.objection2, f.objection3].filter(Boolean),
    painPoints: [f.pain1, f.pain2, f.pain3].filter(Boolean),
    valueProps: [f.value1, f.value2, f.value3].filter(Boolean),
    prospect_mode: f.prospectMode,
    datamoon_filters: f.prospectMode === "datamoon" ? (f.datamoonFilters ?? null) : null,
    datamoon_limit:
      f.prospectMode === "datamoon" && f.datamoonLimit.trim()
        ? Number(f.datamoonLimit)
        : null,
  };
}

function normaliseProfiles(raw: unknown): IcpProfile[] {
  if (Array.isArray(raw)) return raw as IcpProfile[];
  if (raw && typeof raw === "object" && "items" in raw)
    return (raw as { items: IcpProfile[] }).items ?? [];
  return [];
}

/* ── Page ──────────────────────────────────────────────────────────── */

export function IcpProfilesPage() {
  const qc = useQueryClient();

  /* ── Dialog state ── */
  const [crudOpen, setCrudOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<IcpFormState>(EMPTY_FORM);

  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverMode, setDiscoverMode] = useState<"website" | "description">(
    "website"
  );
  const [discoverWebsite, setDiscoverWebsite] = useState("");
  const [discoverDescription, setDiscoverDescription] = useState("");
  const [discoverSave, setDiscoverSave] = useState(true);
  const [discoverResult, setDiscoverResult] = useState<unknown>(null);
  const [discovering, setDiscovering] = useState(false);

  const [suggesting, setSuggesting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<IcpProfile | null>(null);
  const [datamoonTarget, setDatamoonTarget] = useState<IcpProfile | null>(null);
  // Track the preview audience so ICP save can import without rebuilding
  const pendingAudienceRef = useRef<{ audienceId: number; limit: number } | null>(null);
  // True while save + prospect import is in flight (keeps dialog open + button spinning)
  const [_isSavingFull, setIsSavingFull] = useState(false);

  /* ── Queries ── */
  const profilesQ = useQuery<IcpProfile[]>({
    queryKey: ["icp-profiles"],
    queryFn: () =>
      http.get<unknown>("/api/v1/icp-profiles").then(normaliseProfiles),
  });
  const profiles = profilesQ.data ?? [];

  const llmQ = useQuery<LlmConfig[]>({
    queryKey: ["llm-configs"],
    queryFn: () =>
      http
        .get<unknown>("/api/v1/llm-configs")
        .then((r) => (Array.isArray(r) ? r : (r as { items?: LlmConfig[] })?.items ?? [])),
  });
  const hasLlm = (llmQ.data ?? []).some(
    (c) => (c.isActive ?? c.is_active) !== false
  );

  /* ── Mutations ── */
  const saveMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formToPayload> & { id?: string }) => {
      const { id, ...body } = payload;
      if (id) return http.put<IcpProfile>(`/api/v1/icp-profiles/${id}`, body);
      return http.post<IcpProfile>("/api/v1/icp-profiles", body);
    },
    onSuccess: async (savedProfile: IcpProfile) => {
      const action = editingId ? "updated" : "saved";
      qc.invalidateQueries({ queryKey: ["icp-profiles"] });

      // If a DataMoon preview was run, import prospects using the pre-built
      // audience — no need to rebuild. This runs after the ICP id is known.
      const pending = pendingAudienceRef.current;
      if (pending && savedProfile?.id) {
        pendingAudienceRef.current = null;
        try {
          const result = await datamoonApi.importFromAudience({
            audience_id: pending.audienceId,
            icp_id: savedProfile.id,
            limit: pending.limit,
          });
          const imported = result.imported ?? 0;
          const skipped = (result.skipped_duplicate ?? 0) + (result.skipped_no_name ?? 0);
          if (imported > 0) {
            toast.success(
              `ICP ${action} · ${imported} prospect${imported !== 1 ? "s" : ""} imported`,
              { description: skipped > 0 ? `${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped` : undefined }
            );
          } else if (skipped > 0) {
            toast.warning(
              `ICP ${action} · No new prospects`,
              { description: `All ${skipped} prospect${skipped !== 1 ? "s" : ""} already exist in your database` }
            );
          } else {
            toast.success(`ICP ${action}`);
          }
          qc.invalidateQueries({ queryKey: ["prospects"], exact: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          toast.success(`ICP ${action}`);
          toast.error(`Prospect import failed: ${msg}`);
        }
      } else {
        toast.success(`ICP ${action}`);
      }

      // Close dialog only after everything (ICP save + prospect import) is done
      setIsSavingFull(false);
      setCrudOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
    },
    onError: () => {
      setIsSavingFull(false);
      toast.error("Failed to save ICP profile");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => http.delete(`/api/v1/icp-profiles/${id}`),
    onSuccess: () => {
      toast.success("ICP profile deleted");
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["icp-profiles"] });
    },
    onError: () => toast.error("Delete failed"),
  });

  /* ── Handlers ── */
  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCrudOpen(true);
  }

  function openEdit(p: IcpProfile) {
    setEditingId(p.id);
    setForm(profileToForm(p));
    setCrudOpen(true);
  }

  function handleSave() {
    if (!form.name.trim()) {
      toast.error("Profile name is required");
      return;
    }
    // Sourced Prospecting requires an industry filter
    if (form.prospectMode === "datamoon") {
      const hasIndustry = (form.datamoonFilters ?? []).some(
        (f: DmFilter) => f.field === "primary_industry" && f.value
      );
      if (!hasIndustry) {
        toast.error("Industry is required for Sourced Prospecting. Please select an industry.");
        return;
      }
    }
    const payload = formToPayload(form);
    setIsSavingFull(true);
    saveMutation.mutate(editingId ? { ...payload, id: editingId } : payload);
  }

  async function handleAiSuggest() {
    if (!form.persona.trim()) {
      toast.error("Enter a persona description first, then click AI Suggest");
      return;
    }
    setSuggesting(true);
    try {
      const data = await http.post<{
        suggestions?: {
          painPoints?: string[];
          objections?: string[];
          valueProps?: string[];
          proofMetric?: string;
        };
        painPoints?: string[];
        topObjections?: string[];
        valueProps?: string[];
        proofMetric?: string;
      }>("/api/v1/icp-profiles/suggest", {
        seed: form.persona,
      });
      // Support both wrapped (data.suggestions) and flat response shapes
      const s = (data as { suggestions?: typeof data }).suggestions ?? data;
      const pains = s.painPoints ?? [];
      const objections = s.topObjections ?? (s as { objections?: string[] }).objections ?? [];
      const values = s.valueProps ?? [];
      setForm((f) => ({
        ...f,
        pain1: pains[0] ?? f.pain1,
        pain2: pains[1] ?? f.pain2,
        pain3: pains[2] ?? f.pain3,
        objection1: objections[0] ?? f.objection1,
        objection2: objections[1] ?? f.objection2,
        objection3: objections[2] ?? f.objection3,
        value1: values[0] ?? f.value1,
        value2: values[1] ?? f.value2,
        value3: values[2] ?? f.value3,
        proofMetric: (s.proofMetric ?? f.proofMetric) || f.proofMetric,
      }));
      toast.success("AI suggestions applied! Review and adjust as needed.");
    } catch {
      toast.error("AI suggestion failed");
    }
    setSuggesting(false);
  }

  function resetDiscover() {
    setDiscoverMode("website");
    setDiscoverWebsite("");
    setDiscoverDescription("");
    setDiscoverSave(true);
    setDiscoverResult(null);
  }

  async function handleAutoDiscover() {
    if (discoverMode === "website" && !discoverWebsite.trim()) {
      toast.error("Enter your website URL");
      return;
    }
    if (discoverMode === "description" && discoverDescription.trim().length < 10) {
      toast.error("Enter at least 10 characters describing your ICP");
      return;
    }
    setDiscovering(true);
    setDiscoverResult(null);
    try {
      // Backend /auto-discover accepts a list of prospects. For the
      // website/description flow we send a synthetic single-prospect payload
      // with the input embedded in the description field.
      const syntheticProspect =
        discoverMode === "website"
          ? { description: `website: ${discoverWebsite.trim()}` }
          : { description: discoverDescription.trim() };

      const data = await http.post<unknown>(
        "/api/v1/icp-profiles/auto-discover",
        {
          prospects: [syntheticProspect],
          existingIcpId: null,
        }
      );
      setDiscoverResult(data);

      if (discoverSave) {
        // Auto-save: derive a profile from the response and POST it
        const r = data as {
          suggestedPersona?: string;
          commonAttributes?: Record<string, unknown>;
        };
        const name =
          discoverMode === "website"
            ? `Profile from ${discoverWebsite.trim()}`
            : "Auto-Discovered ICP";
        await http.post<IcpProfile>("/api/v1/icp-profiles", {
          name,
          persona: r.suggestedPersona ?? "",
          companyType:
            String(r.commonAttributes?.companyType ?? "") || null,
          painPoints: [],
          topObjections: [],
          valueProps: [],
        });
        toast.success(`Auto-discovered ICP saved — "${name}"`);
        qc.invalidateQueries({ queryKey: ["icp-profiles"] });
        setTimeout(() => {
          setDiscoverOpen(false);
          resetDiscover();
        }, 2500);
      } else {
        toast.success("Auto-discovery complete — review the result below");
      }
    } catch {
      toast.error("Auto-discovery failed");
    }
    setDiscovering(false);
  }

  async function handleSaveDiscoveredProfile(profileData: {
    name?: string;
    persona?: string;
    companyType?: string | null;
  }) {
    try {
      await http.post<IcpProfile>("/api/v1/icp-profiles", {
        name: profileData.name ?? "Auto-Discovered ICP",
        persona: profileData.persona ?? "",
        companyType: profileData.companyType ?? null,
        painPoints: [],
        topObjections: [],
        valueProps: [],
      });
      toast.success(`Saved "${profileData.name ?? "ICP"}" to your list`);
      qc.invalidateQueries({ queryKey: ["icp-profiles"] });
      setDiscoverOpen(false);
      resetDiscover();
    } catch {
      toast.error("Failed to save profile");
    }
  }

  /* ── Render ── */
  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="ICP Profiles"
        description="Define your Ideal Customer Profiles for targeted outreach"
        actions={
          <div className="flex items-center gap-2">
            {/* Auto-Discover ICP */}
            <Dialog
              open={discoverOpen}
              onOpenChange={(o) => {
                setDiscoverOpen(o);
                if (!o) resetDiscover();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  onClick={() => {
                    resetDiscover();
                    setDiscoverOpen(true);
                  }}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Auto-Discover ICP
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Auto-Discover ICP</DialogTitle>
                  <DialogDescription>
                    Let the AI infer your Ideal Customer Profile — the fastest
                    way to get started.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* LLM warning */}
                  {!hasLlm && (
                    <div className="rounded-md bg-amber-50 border border-amber-300 p-4 text-sm space-y-2">
                      <p className="font-medium text-amber-900 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        No AI model configured
                      </p>
                      <p className="text-amber-800 text-xs">
                        Auto-Discover needs an LLM. Configure one in LLM
                        Models first.
                      </p>
                    </div>
                  )}

                  {/* Mode tabs */}
                  <Tabs
                    value={discoverMode}
                    onValueChange={(v) => {
                      setDiscoverMode(v as "website" | "description");
                      setDiscoverResult(null);
                    }}
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="website">
                        <Globe className="h-3.5 w-3.5 mr-1.5" />
                        From your website
                      </TabsTrigger>
                      <TabsTrigger value="description">
                        <FileText className="h-3.5 w-3.5 mr-1.5" />
                        From description
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="website" className="space-y-3 mt-4">
                      <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800">
                        <p className="font-medium mb-1">How it works</p>
                        <p>
                          Enter your company URL. The AI reads your website,
                          searches for competitors and industry context, and
                          infers your ICP automatically — persona, company
                          type, pain points, and value propositions.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Your Website URL</Label>
                        <Input
                          placeholder="e.g. mycompany.com"
                          value={discoverWebsite}
                          onChange={(e) => setDiscoverWebsite(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          No need to include https:// — we'll strip it.
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent
                      value="description"
                      className="space-y-3 mt-4"
                    >
                      <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-xs text-violet-800">
                        <p className="font-medium mb-1">How it works</p>
                        <p>
                          Describe your ideal customer in plain English. The
                          AI structures it into a full ICP profile — persona,
                          company type, pain points, objections, and value
                          propositions.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Describe your ideal customer</Label>
                        <Textarea
                          placeholder="e.g. We sell to VP of Engineering at Series B SaaS companies (50–500 employees) who are struggling with slow release cycles..."
                          value={discoverDescription}
                          onChange={(e) =>
                            setDiscoverDescription(e.target.value)
                          }
                          rows={5}
                        />
                        <p className="text-xs text-muted-foreground">
                          The more specific you are, the better the AI result.
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <Separator />

                  {/* Save toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">
                        Save profiles automatically
                      </p>
                      <p className="text-xs text-muted-foreground">
                        If on, discovered profiles are saved immediately. If
                        off, you can review first.
                      </p>
                    </div>
                    <Switch
                      checked={discoverSave}
                      onCheckedChange={setDiscoverSave}
                    />
                  </div>

                  {/* Result */}
                  {Boolean(discoverResult) && (
                    <DiscoverResult
                      result={discoverResult as DiscoverResultShape}
                      discoverMode={discoverMode}
                      discoverSave={discoverSave}
                      discoverWebsite={discoverWebsite}
                      onSaveProfile={handleSaveDiscoveredProfile}
                    />
                  )}
                </div>

                <DialogFooter>
                  {discovering && (
                    <p className="text-xs text-muted-foreground mr-auto flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {discoverMode === "website"
                        ? "Searching the web + AI analysis (30–60 s)…"
                        : "AI analyzing your description (10–20 s)…"}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDiscoverOpen(false);
                      resetDiscover();
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={handleAutoDiscover}
                    disabled={discovering || !hasLlm}
                  >
                    {discovering ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Discovering…
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                        Run Auto-Discovery
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Create ICP (manual) */}
            <Dialog
              open={crudOpen}
              onOpenChange={(o) => {
                setCrudOpen(o);
                if (!o) {
                  setForm(EMPTY_FORM);
                  setEditingId(null);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create ICP
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <IcpFormDialog
                  form={form}
                  setForm={setForm}
                  editingId={editingId}
                  suggesting={suggesting}
                  saving={saveMutation.isPending}
                  hasLlm={hasLlm}
                  onAiSuggest={handleAiSuggest}
                  onSave={handleSave}
                  onClose={() => {
                    setCrudOpen(false);
                    setForm(EMPTY_FORM);
                    setEditingId(null);
                  }}
                  onAudiencePreviewReady={(audienceId, limit) => {
                    pendingAudienceRef.current = { audienceId, limit };
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* LLM warning banner */}
      {!hasLlm && !llmQ.isLoading && (
        <div className="rounded-md bg-amber-50 border border-amber-300 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-900">
              No AI model configured
            </p>
            <p className="text-xs text-amber-800">
              AI Suggest and Auto-Discover require an active LLM. Configure
              one in{" "}
              <span className="font-semibold">Setup → LLM Models</span>.
            </p>
          </div>
          <Bot className="h-5 w-5 text-amber-500 ml-auto shrink-0" />
        </div>
      )}

      {/* Profile grid */}
      {profilesQ.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-48 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : profilesQ.isError ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Failed to load ICP profiles.
            </p>
            <Button
              onClick={() => profilesQ.refetch()}
              className="mt-4"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : profiles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h4 className="font-medium mb-2">No ICP Profiles Yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first ICP to start targeting the right prospects.
            </p>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              Create ICP
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <IcpCard
              key={p.id}
              profile={p}
              onEdit={() => openEdit(p)}
              onDelete={() => setDeleteTarget(p)}
              onDataMoon={() => setDatamoonTarget(p)}
            />
          ))}
        </div>
      )}

      {/* DataMoon source dialog */}
      <Dialog
        open={!!datamoonTarget}
        onOpenChange={(o) => !o && setDatamoonTarget(null)}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Source Prospects
            </DialogTitle>
            <DialogDescription>
              {datamoonTarget?.name
                ? `Build a sourced audience for "${datamoonTarget.name}" and import matching prospects.`
                : "Build a sourced audience and import matching prospects."}
            </DialogDescription>
          </DialogHeader>
          <DataMoonSourcePanel
            icp={datamoonTarget}
            initialLimit={
              datamoonTarget?.datamoon_limit != null
                ? String(datamoonTarget.datamoon_limit)
                : undefined
            }
            onImported={() => {
              qc.invalidateQueries({ queryKey: ["prospects"] });
              qc.invalidateQueries({ queryKey: ["icp-profiles"] });
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete ICP profile?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.name
                ? `"${deleteTarget.name}" will be permanently removed. Prospects linked to this profile will lose their ICP scoring. This action cannot be undone.`
                : "This ICP profile will be permanently removed. This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget.id)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── IcpCard ────────────────────────────────────────────────────────── */

function IcpCard({
  profile: p,
  onEdit,
  onDelete,
  onDataMoon: _onDataMoon,
}: {
  profile: IcpProfile;
  onEdit: () => void;
  onDelete: () => void;
  onDataMoon: () => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <CardTitle className="text-sm truncate">{p.name}</CardTitle>
            {p.prospectCount != null && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0">
                <Users className="h-2.5 w-2.5" />
                {p.prospectCount}
              </span>
            )}
            {p.prospect_mode && p.prospect_mode !== "manual" && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                p.prospect_mode === "datamoon" ? "bg-violet-50 text-violet-700 border border-violet-200" :
                p.prospect_mode === "linkedin" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                "bg-amber-50 text-amber-700 border border-amber-200"
              }`}>
                {p.prospect_mode === "datamoon" ? "Sourced Prospecting" :
                 p.prospect_mode === "linkedin" ? "LinkedIn" :
                 p.prospect_mode === "web_scraping" ? "Web Scraping" : p.prospect_mode}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                  onClick={onDataMoon}
                  aria-label="Source prospects"
                >
                  <Database className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Source prospects</TooltipContent>
            </Tooltip> */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={onEdit}
                  aria-label="Edit"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit this ICP profile</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                  onClick={onDelete}
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete this ICP profile</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <CardDescription className="text-xs">
          {p.companyType ?? "No company type"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        {p.persona && (
          <p className="text-muted-foreground line-clamp-2">{p.persona}</p>
        )}
        {p.topObjections.length > 0 && (
          <div>
            <p className="font-medium mb-1">Objections:</p>
            <div className="flex flex-wrap gap-1">
              {p.topObjections.map((o, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {o}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {p.painPoints.length > 0 && (
          <div>
            <p className="font-medium mb-1">Pain Points:</p>
            <div className="flex flex-wrap gap-1">
              {p.painPoints.map((pn, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {pn}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {p.valueProps.length > 0 && (
          <div>
            <p className="font-medium mb-1">Value Props:</p>
            <div className="flex flex-wrap gap-1">
              {p.valueProps.map((v, i) => (
                <Badge
                  key={i}
                  className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200"
                >
                  {v}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {(p.senderCompany ?? p.senderRole) && (
          <p className="text-muted-foreground mt-1">
            From:{" "}
            {[p.senderRole, p.senderCompany].filter(Boolean).join(" at ")}
          </p>
        )}
        {p.proofMetric && (
          <p className="text-muted-foreground">Proof: {p.proofMetric}</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Prospect Mode options ──────────────────────────────────────────── */

const PROSPECT_MODES = [
  { value: "manual", label: "Manual", description: "Add prospects manually or import a CSV" },
  { value: "datamoon", label: "Sourced Prospecting", description: "Auto-source from B2B database" },
  { value: "web_scraping", label: "Web Scraping", description: "Scrape prospects from the web" },
  // { value: "linkedin", label: "LinkedIn", description: "Source from LinkedIn search" },
] as const;

/* ── DataMoonFiltersForm — lightweight filter editor for ICP creation ─ */

const DM_INDUSTRY_OPTIONS = [
  "Accounting", "Airlines/Aviation", "Alternative Dispute Resolution",
  "Alternative Medicine", "Animation", "Apparel & Fashion", "Architecture & Planning",
  "Arts and Crafts", "Automotive", "Aviation & Aerospace", "Banking", "Biotechnology",
  "Broadcast Media", "Building Materials", "Business Supplies and Equipment",
  "Capital Markets", "Chemicals", "Civic & Social Organization", "Civil Engineering",
  "Commercial Real Estate", "Computer & Network Security", "Computer Games",
  "Computer Hardware", "Computer Networking", "Computer Software", "Construction",
  "Consumer Electronics", "Consumer Goods", "Consumer Services", "Cosmetics",
  "Dairy", "Defense & Space", "Design", "Education Management",
  "E-Learning", "Electrical/Electronic Manufacturing", "Entertainment",
  "Environmental Services", "Events Services", "Executive Office",
  "Facilities Services", "Farming", "Financial Services", "Fine Art",
  "Fishery", "Food & Beverages", "Food Production", "Fund-Raising",
  "Furniture", "Gambling & Casinos", "Glass, Ceramics & Concrete",
  "Government Administration", "Government Relations", "Graphic Design",
  "Health, Wellness and Fitness", "Higher Education", "Hospital & Health Care",
  "Hospitality", "Human Resources", "Import and Export", "Individual & Family Services",
  "Industrial Automation", "Information Services", "Information Technology and Services",
  "Insurance", "International Affairs", "International Trade and Development",
  "Internet", "Investment Banking", "Investment Management", "Judiciary",
  "Law Enforcement", "Law Practice", "Legal Services", "Legislative Office",
  "Leisure, Travel & Tourism", "Libraries", "Logistics and Supply Chain",
  "Luxury Goods & Jewelry", "Machinery", "Management Consulting",
  "Maritime", "Market Research", "Marketing and Advertising",
  "Mechanical or Industrial Engineering", "Media Production", "Medical Devices",
  "Medical Practice", "Mental Health Care", "Military", "Mining & Metals",
  "Motion Pictures and Film", "Museums and Institutions", "Music",
  "Nanotechnology", "Newspapers", "Nonprofit Organization Management",
  "Oil & Energy", "Online Media", "Outsourcing/Offshoring", "Package/Freight Delivery",
  "Packaging and Containers", "Paper & Forest Products", "Performing Arts",
  "Pharmaceuticals", "Philanthropy", "Photography", "Plastics",
  "Political Organization", "Primary/Secondary Education", "Printing",
  "Professional Training & Coaching", "Program Development", "Public Policy",
  "Public Relations and Communications", "Public Safety", "Publishing",
  "Railroad Manufacture", "Ranching", "Real Estate",
  "Recreational Facilities and Services", "Religious Institutions",
  "Renewables & Environment", "Research", "Restaurants", "Retail",
  "Security and Investigations", "Semiconductors", "Shipbuilding",
  "Sporting Goods", "Sports", "Staffing and Recruiting", "Supermarkets",
  "Telecommunications", "Textiles", "Think Tanks", "Tobacco",
  "Translation and Localization", "Transportation/Trucking/Railroad",
  "Utilities", "Venture Capital & Private Equity", "Veterinary",
  "Warehousing", "Wholesale", "Wine and Spirits", "Wireless",
  "Writing and Editing",
];

// Values match DataMoon API exactly (same as DataMoonSourcePanel)
const DM_SIZE_OPTIONS = [
  { value: "1 to 10",        label: "1 – 10" },
  { value: "11 to 25",       label: "11 – 25" },
  { value: "26 to 50",       label: "26 – 50" },
  { value: "51 to 100",      label: "51 – 100" },
  { value: "101 to 250",     label: "101 – 250" },
  { value: "251 to 500",     label: "251 – 500" },
  { value: "501 to 1000",    label: "501 – 1,000" },
  { value: "1001 to 5000",   label: "1,001 – 5,000" },
  { value: "5001 to 10000",  label: "5,001 – 10,000" },
  { value: "10000+",         label: "10,000+" },
];

const DM_SENIORITY_OPTIONS = [
  { value: "cxo",                          label: "C-Level / CxO" },
  { value: "vice president",               label: "VP" },
  { value: "director",                     label: "Director" },
  { value: "manager",                      label: "Manager" },
  { value: "senior individual contributor",label: "Senior IC" },
  { value: "partner",                      label: "Partner" },
  { value: "owner",                        label: "Owner" },
];

const DM_REVENUE_OPTIONS = [
  { value: "under 1 million",             label: "Under $1M" },
  { value: "1 million to 5 million",      label: "$1M – $5M" },
  { value: "5 million to 10 million",     label: "$5M – $10M" },
  { value: "10 million to 25 million",    label: "$10M – $25M" },
  { value: "25 million to 50 million",    label: "$25M – $50M" },
  { value: "50 million to 100 million",   label: "$50M – $100M" },
  { value: "100 million to 250 million",  label: "$100M – $250M" },
  { value: "250 million to 500 million",  label: "$250M – $500M" },
  { value: "500 million to 1 billion",    label: "$500M – $1B" },
  { value: "1 billion and over",          label: "$1B+" },
];

const DM_US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina",
  "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island",
  "South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
  "District of Columbia","Puerto Rico","U.S. Virgin Islands",
];

/* ── Industry → NAICS suggestions map ───────────────────────────────── */
type NaicsEntry = { code: string; title: string };

const INDUSTRY_NAICS_MAP: Record<string, NaicsEntry[]> = {
  "Information Technology and Services": [
    { code: "541511", title: "Custom Computer Programming Services" },
    { code: "541512", title: "Computer Systems Design Services" },
    { code: "541519", title: "Other Computer Related Services" },
    { code: "518210", title: "Data Processing & Hosting Services" },
    { code: "541690", title: "Other Scientific & Technical Consulting" },
  ],
  "Computer Software": [
    { code: "511210", title: "Software Publishers" },
    { code: "541511", title: "Custom Computer Programming Services" },
    { code: "541512", title: "Computer Systems Design Services" },
    { code: "518210", title: "Data Processing & Hosting Services" },
  ],
  "Computer Hardware": [
    { code: "334111", title: "Electronic Computer Manufacturing" },
    { code: "334112", title: "Computer Storage Device Manufacturing" },
    { code: "334118", title: "Computer Terminal & Peripheral Mfg" },
    { code: "423430", title: "Computer & Peripheral Equipment Wholesale" },
  ],
  "Computer Networking": [
    { code: "517110", title: "Wired Telecommunications Carriers" },
    { code: "517210", title: "Wireless Telecommunications" },
    { code: "541512", title: "Computer Systems Design Services" },
    { code: "334210", title: "Telephone Apparatus Manufacturing" },
  ],
  "Computer & Network Security": [
    { code: "541512", title: "Computer Systems Design Services" },
    { code: "541519", title: "Other Computer Related Services" },
    { code: "561621", title: "Security Systems Services" },
    { code: "541690", title: "Other Scientific & Technical Consulting" },
  ],
  "Internet": [
    { code: "519130", title: "Internet Publishing & Broadcasting" },
    { code: "518210", title: "Data Processing & Hosting Services" },
    { code: "454110", title: "Electronic Shopping" },
    { code: "541511", title: "Custom Computer Programming Services" },
  ],
  "Financial Services": [
    { code: "522110", title: "Commercial Banking" },
    { code: "522120", title: "Savings Institutions" },
    { code: "523110", title: "Investment Banking & Securities Dealing" },
    { code: "523120", title: "Securities Brokerage" },
    { code: "524113", title: "Direct Life Insurance Carriers" },
    { code: "525990", title: "Other Financial Vehicles" },
  ],
  "Banking": [
    { code: "522110", title: "Commercial Banking" },
    { code: "522120", title: "Savings Institutions" },
    { code: "522130", title: "Credit Unions" },
    { code: "522190", title: "Other Depository Credit Intermediation" },
  ],
  "Investment Banking": [
    { code: "523110", title: "Investment Banking & Securities Dealing" },
    { code: "523120", title: "Securities Brokerage" },
    { code: "523920", title: "Portfolio Management" },
    { code: "523930", title: "Investment Advice" },
  ],
  "Investment Management": [
    { code: "523920", title: "Portfolio Management" },
    { code: "523930", title: "Investment Advice" },
    { code: "525110", title: "Pension Funds" },
    { code: "525190", title: "Other Insurance Funds" },
  ],
  "Venture Capital & Private Equity": [
    { code: "523910", title: "Miscellaneous Intermediation" },
    { code: "525910", title: "Open-End Investment Funds" },
    { code: "523120", title: "Securities Brokerage" },
  ],
  "Insurance": [
    { code: "524113", title: "Direct Life Insurance Carriers" },
    { code: "524114", title: "Direct Health & Medical Insurance" },
    { code: "524126", title: "Direct Property & Casualty Insurance" },
    { code: "524210", title: "Insurance Agencies & Brokerages" },
  ],
  "Hospital & Health Care": [
    { code: "622110", title: "General Medical & Surgical Hospitals" },
    { code: "621111", title: "Offices of Physicians (except Mental Health)" },
    { code: "621210", title: "Offices of Dentists" },
    { code: "621610", title: "Home Health Care Services" },
    { code: "623110", title: "Nursing Care Facilities" },
  ],
  "Medical Devices": [
    { code: "339112", title: "Surgical & Medical Instrument Mfg" },
    { code: "339113", title: "Surgical Appliance & Supplies Mfg" },
    { code: "339114", title: "Dental Equipment & Supplies Mfg" },
    { code: "334510", title: "Electromedical Apparatus Mfg" },
  ],
  "Pharmaceuticals": [
    { code: "325411", title: "Medicinal & Botanical Manufacturing" },
    { code: "325412", title: "Pharmaceutical Preparation Manufacturing" },
    { code: "325413", title: "In-Vitro Diagnostic Substance Mfg" },
    { code: "424210", title: "Drugs & Druggists' Sundries Wholesale" },
  ],
  "Biotechnology": [
    { code: "541714", title: "Research & Development in Biotechnology" },
    { code: "325411", title: "Medicinal & Botanical Manufacturing" },
    { code: "325412", title: "Pharmaceutical Preparation Manufacturing" },
  ],
  "Management Consulting": [
    { code: "541611", title: "Administrative Management Consulting" },
    { code: "541612", title: "Human Resources Consulting" },
    { code: "541613", title: "Marketing Consulting Services" },
    { code: "541614", title: "Process, Physical Distribution Consulting" },
    { code: "541618", title: "Other Management Consulting Services" },
  ],
  "Marketing and Advertising": [
    { code: "541810", title: "Advertising Agencies" },
    { code: "541820", title: "Public Relations Agencies" },
    { code: "541830", title: "Media Buying Agencies" },
    { code: "541850", title: "Outdoor Advertising" },
    { code: "541890", title: "Other Services Related to Advertising" },
  ],
  "Public Relations and Communications": [
    { code: "541820", title: "Public Relations Agencies" },
    { code: "541890", title: "Other Services Related to Advertising" },
    { code: "519110", title: "News Syndicates" },
  ],
  "Staffing and Recruiting": [
    { code: "561310", title: "Employment Placement Agencies" },
    { code: "561320", title: "Temporary Help Services" },
    { code: "561330", title: "Professional Employer Organizations" },
  ],
  "Human Resources": [
    { code: "541612", title: "Human Resources Consulting" },
    { code: "561310", title: "Employment Placement Agencies" },
    { code: "561320", title: "Temporary Help Services" },
  ],
  "Real Estate": [
    { code: "531110", title: "Lessors of Residential Buildings" },
    { code: "531120", title: "Lessors of Nonresidential Buildings" },
    { code: "531210", title: "Offices of Real Estate Agents & Brokers" },
    { code: "531390", title: "Other Activities Related to Real Estate" },
  ],
  "Commercial Real Estate": [
    { code: "531120", title: "Lessors of Nonresidential Buildings" },
    { code: "531190", title: "Lessors of Other Real Estate Property" },
    { code: "531210", title: "Offices of Real Estate Agents & Brokers" },
  ],
  "Construction": [
    { code: "236110", title: "Residential Building Construction" },
    { code: "236220", title: "Commercial & Institutional Building Construction" },
    { code: "237110", title: "Water & Sewer Line Construction" },
    { code: "238910", title: "Site Preparation Contractors" },
  ],
  "Civil Engineering": [
    { code: "541330", title: "Engineering Services" },
    { code: "237310", title: "Highway, Street & Bridge Construction" },
    { code: "237990", title: "Other Heavy & Civil Engineering" },
  ],
  "Architecture & Planning": [
    { code: "541310", title: "Architectural Services" },
    { code: "541320", title: "Landscape Architecture Services" },
    { code: "541330", title: "Engineering Services" },
    { code: "541340", title: "Drafting Services" },
  ],
  "Education Management": [
    { code: "611110", title: "Elementary & Secondary Schools" },
    { code: "611210", title: "Junior Colleges" },
    { code: "611310", title: "Colleges, Universities & Professional Schools" },
    { code: "611699", title: "Other Technical & Trade Schools" },
  ],
  "Higher Education": [
    { code: "611310", title: "Colleges, Universities & Professional Schools" },
    { code: "611210", title: "Junior Colleges" },
    { code: "611420", title: "Computer Training" },
  ],
  "E-Learning": [
    { code: "611420", title: "Computer Training" },
    { code: "611710", title: "Educational Support Services" },
    { code: "519130", title: "Internet Publishing & Broadcasting" },
  ],
  "Professional Training & Coaching": [
    { code: "611430", title: "Professional & Management Development Training" },
    { code: "611699", title: "Other Technical & Trade Schools" },
    { code: "611710", title: "Educational Support Services" },
  ],
  "Retail": [
    { code: "441110", title: "New Car Dealers" },
    { code: "452110", title: "Department Stores" },
    { code: "454110", title: "Electronic Shopping" },
    { code: "445110", title: "Supermarkets & Grocery Stores" },
  ],
  "Consumer Goods": [
    { code: "311", title: "Food Manufacturing" },
    { code: "314", title: "Textile Product Mills" },
    { code: "315", title: "Apparel Manufacturing" },
    { code: "423", title: "Merchant Wholesalers, Durable Goods" },
  ],
  "Consumer Electronics": [
    { code: "334310", title: "Audio & Video Equipment Manufacturing" },
    { code: "334220", title: "Radio & TV Broadcasting Equipment Mfg" },
    { code: "423690", title: "Electronic Parts & Equipment Wholesale" },
  ],
  "Telecommunications": [
    { code: "517110", title: "Wired Telecommunications Carriers" },
    { code: "517210", title: "Wireless Telecommunications" },
    { code: "517410", title: "Satellite Telecommunications" },
    { code: "517919", title: "Other Telecommunications" },
  ],
  "Logistics and Supply Chain": [
    { code: "484110", title: "General Freight Trucking, Local" },
    { code: "484121", title: "General Freight Trucking, Long-Distance" },
    { code: "493110", title: "General Warehousing & Storage" },
    { code: "541614", title: "Process, Physical Distribution Consulting" },
  ],
  "Transportation/Trucking/Railroad": [
    { code: "484110", title: "General Freight Trucking, Local" },
    { code: "484121", title: "General Freight Trucking, Long-Distance" },
    { code: "482110", title: "Rail Transportation" },
    { code: "488510", title: "Freight Transportation Arrangement" },
  ],
  "Oil & Energy": [
    { code: "211120", title: "Crude Petroleum Extraction" },
    { code: "211130", title: "Natural Gas Extraction" },
    { code: "221112", title: "Fossil Fuel Electric Power Generation" },
    { code: "324110", title: "Petroleum Refineries" },
  ],
  "Renewables & Environment": [
    { code: "221114", title: "Solar Electric Power Generation" },
    { code: "221115", title: "Wind Electric Power Generation" },
    { code: "562910", title: "Remediation Services" },
    { code: "541620", title: "Environmental Consulting Services" },
  ],
  "Automotive": [
    { code: "336111", title: "Automobile Manufacturing" },
    { code: "336310", title: "Motor Vehicle Gasoline Engine Parts Mfg" },
    { code: "441110", title: "New Car Dealers" },
    { code: "811111", title: "General Automotive Repair" },
  ],
  "Accounting": [
    { code: "541211", title: "Offices of Certified Public Accountants" },
    { code: "541213", title: "Tax Preparation Services" },
    { code: "541214", title: "Payroll Services" },
    { code: "541219", title: "Other Accounting Services" },
  ],
  "Legal Services": [
    { code: "541110", title: "Offices of Lawyers" },
    { code: "541120", title: "Offices of Notaries" },
    { code: "541190", title: "Other Legal Services" },
  ],
  "Law Practice": [
    { code: "541110", title: "Offices of Lawyers" },
    { code: "541190", title: "Other Legal Services" },
  ],
  "Restaurants": [
    { code: "722511", title: "Full-Service Restaurants" },
    { code: "722513", title: "Limited-Service Restaurants" },
    { code: "722515", title: "Snack & Nonalcoholic Beverage Bars" },
  ],
  "Food & Beverages": [
    { code: "311110", title: "Animal Food Manufacturing" },
    { code: "311800", title: "Bakeries & Tortilla Manufacturing" },
    { code: "312110", title: "Soft Drink & Ice Manufacturing" },
    { code: "311900", title: "Other Food Manufacturing" },
  ],
  "Hospitality": [
    { code: "721110", title: "Hotels (except Casino Hotels)" },
    { code: "721120", title: "Casino Hotels" },
    { code: "722511", title: "Full-Service Restaurants" },
    { code: "721310", title: "Rooming & Boarding Houses" },
  ],
  "Media Production": [
    { code: "512110", title: "Motion Picture & Video Production" },
    { code: "512191", title: "Teleproduction & Post-Production Services" },
    { code: "515112", title: "Radio Stations" },
    { code: "515120", title: "Television Broadcasting" },
  ],
  "Publishing": [
    { code: "511110", title: "Newspaper Publishers" },
    { code: "511120", title: "Periodical Publishers" },
    { code: "511130", title: "Book Publishers" },
    { code: "511210", title: "Software Publishers" },
  ],
  "Nonprofit Organization Management": [
    { code: "813110", title: "Religious Organizations" },
    { code: "813211", title: "Grantmaking Foundations" },
    { code: "813212", title: "Voluntary Health Organizations" },
    { code: "813319", title: "Other Social Advocacy Organizations" },
  ],
  "Research": [
    { code: "541711", title: "Research in Biotechnology" },
    { code: "541712", title: "Research in Physical, Engineering Sciences" },
    { code: "541720", title: "Research in Social Sciences & Humanities" },
  ],
  "Market Research": [
    { code: "541910", title: "Marketing Research & Public Opinion Polling" },
    { code: "541613", title: "Marketing Consulting Services" },
  ],
  "Information Services": [
    { code: "519190", title: "All Other Information Services" },
    { code: "518210", title: "Data Processing & Hosting Services" },
    { code: "541511", title: "Custom Computer Programming Services" },
  ],
  "Events Services": [
    { code: "561920", title: "Convention & Trade Show Organizers" },
    { code: "711310", title: "Promoters of Performing Arts & Sports" },
    { code: "561990", title: "All Other Support Services" },
  ],
  "Design": [
    { code: "541430", title: "Graphic Design Services" },
    { code: "541410", title: "Interior Design Services" },
    { code: "541490", title: "Other Specialized Design Services" },
  ],
  "Graphic Design": [
    { code: "541430", title: "Graphic Design Services" },
    { code: "541490", title: "Other Specialized Design Services" },
  ],
  "Health, Wellness and Fitness": [
    { code: "713940", title: "Fitness & Recreational Sports Centers" },
    { code: "621111", title: "Offices of Physicians" },
    { code: "621999", title: "All Other Ambulatory Health Care Services" },
  ],
  "Mental Health Care": [
    { code: "621330", title: "Offices of Mental Health Practitioners" },
    { code: "623220", title: "Residential Mental Health Facilities" },
    { code: "621420", title: "Outpatient Mental Health Centers" },
  ],
};

type DmFilter = { field: string; operator: string; value: unknown };

/** Extract a single-value filter from the stored list */
function _getVal(filters: DmFilter[], field: string): unknown {
  return filters.find((f) => f.field === field)?.value ?? null;
}
function _getArr(filters: DmFilter[], field: string): string[] {
  const v = _getVal(filters, field);
  return Array.isArray(v) ? (v as string[]) : [];
}
function _getStr(filters: DmFilter[], field: string): string {
  const v = _getVal(filters, field);
  if (typeof v === "string") return v;
  // Industry is stored as a single-element array e.g. ["Financial Services"]
  if (Array.isArray(v) && v.length > 0) return String(v[0]);
  return "";
}

// Additional filter row — predefined field + operator + value
type AdditionalFilter = { id: string; field: string; operator: string; value: string };

const DM_ADDITIONAL_FIELD_OPTIONS = [
  { value: "job_title",          label: "Job Title" },
  { value: "company_sic_code",   label: "Company SIC Code" },
  { value: "company_domain",     label: "Company Domain" },
  { value: "company_name",       label: "Company Name" },
  { value: "personal_state",     label: "Personal State" },
  { value: "professional_state", label: "Professional State" },
  { value: "department",         label: "Department" },
  { value: "homeowner",          label: "Homeowner" },
  { value: "income_range",       label: "Income Range" },
  { value: "net_worth",          label: "Net Worth" },
];

const DM_OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "=",        label: "Equals" },
  { value: "!=",       label: "Not equals" },
  { value: "in",       label: "In" },
  { value: "has_value",label: "Has value" },
];

interface PreviewProspect {
  first_name: string | null;
  last_name: string | null;
  business_email: string | null;
  job_title: string | null;
  company_name: string | null;
  company_domain: string | null;
  seniority_level_2: string | null;
}

function DataMoonFiltersForm({
  filters,
  onChange,
  limit,
  onLimitChange,
  icpId: _icpId,
  onAudiencePreviewReady,
}: {
  filters: DmFilter[] | null;
  onChange: (f: DmFilter[]) => void;
  limit: string;
  onLimitChange: (v: string) => void;
  /** ICP profile id — set after save/edit so import can tag prospects */
  icpId?: string | null;
  /** Called when a preview completes, so the parent can import on ICP-save */
  onAudiencePreviewReady?: (audienceId: number, limit: number) => void;
}) {
  const stored: DmFilter[] = filters ?? [];

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [previewProspects, setPreviewProspects] = useState<PreviewProspect[]>([]);
  const [previewPage, setPreviewPage] = useState(1);
  const [_previewLiveFilters, setPreviewLiveFilters] = useState<DmFilter[]>([]);
  const [_previewAudienceId, setPreviewAudienceId] = useState<number | null>(null);
  // Save state

  const [saveResult, setSaveResult] = useState<{ imported: number; skipped: number } | null>(null);
  const PAGE_SIZE = 10;

  // Quota
  const { data: dmStatus } = useQuery({
    queryKey: ["datamoon-status"],
    queryFn: () => datamoonApi.status(),
    staleTime: 60_000,
  });
  const quotaRemaining =
    dmStatus?.prospect_quota != null && dmStatus?.quota_used != null
      ? dmStatus.prospect_quota - dmStatus.quota_used
      : null;

  // Restore UI state from stored DataMoon filter list
  // primary_industry is DataMoon's confirmed field name
  const storedIndustry = _getStr(stored, "primary_industry");
  const [industry, setIndustry] = useState<string>(
    DM_INDUSTRY_OPTIONS.includes(storedIndustry) ? storedIndustry : ""
  );
  const [customIndustry, setCustomIndustry] = useState<string>(
    DM_INDUSTRY_OPTIONS.includes(storedIndustry) ? "" : storedIndustry
  );
  const [industryTouched, setIndustryTouched] = useState(false);
  const [states, setStates] = useState<string[]>(_getArr(stored, "company_state"));
  const [stateSearch, setStateSearch] = useState("");
  const [seniorities, setSeniorities] = useState<string[]>(_getArr(stored, "seniority_level_2"));
  const [sizes, setSizes] = useState<string[]>(_getArr(stored, "company_employee_count"));
  const [revenues, setRevenues] = useState<string[]>(_getArr(stored, "company_revenue"));
  const [naicsCodes, setNaicsCodes] = useState<string>(
    (() => { const v = _getVal(stored, "company_naics"); return Array.isArray(v) ? (v as string[]).join(", ") : ""; })()
  );
  // Additional Filters accordion
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilter[]>(() => {
    const knownFields = new Set(["business_email", "primary_industry", "company_state", "seniority_level_2", "company_employee_count", "company_revenue", "company_naics"]);
    return stored
      .filter((f) => !knownFields.has(f.field))
      .map((f) => ({
        id: Math.random().toString(36).slice(2),
        field: f.field,
        operator: String(f.operator),
        value: Array.isArray(f.value) ? (f.value as string[]).join(", ") : String(f.value ?? ""),
      }));
  });

  // Build and emit native DataMoon filter list on every change
  useEffect(() => {
    const effectiveIndustry = customIndustry.trim() || industry;
    const items: DmFilter[] = [
      { field: "business_email", operator: "has_value", value: null },
    ];
    if (effectiveIndustry)
      items.push({ field: "primary_industry", operator: "in", value: [effectiveIndustry] });
    if (states.length)
      items.push({ field: "company_state", operator: "in", value: states });
    if (seniorities.length)
      items.push({ field: "seniority_level_2", operator: "in", value: seniorities });
    if (sizes.length)
      items.push({ field: "company_employee_count", operator: "in", value: sizes });
    if (revenues.length)
      items.push({ field: "company_revenue", operator: "in", value: revenues });
    const codes = naicsCodes.split(",").map((c) => c.trim()).filter(Boolean);
    if (codes.length)
      items.push({ field: "company_naics", operator: "in", value: codes });
    // Additional filters
    for (const af of additionalFilters) {
      if (af.field.trim() && af.value.trim()) {
        const vals = af.value.split(",").map((v) => v.trim()).filter(Boolean);
        items.push({ field: af.field.trim(), operator: af.operator || "=", value: vals.length > 1 ? vals : vals[0] ?? "" });
      }
    }
    onChange(items);
  }, [industry, customIndustry, states, naicsCodes, seniorities, sizes, revenues, additionalFilters]);

  function toggleArr<T>(arr: T[], val: T): T[] {
    return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
  }

  function addFilter() {
    setAdditionalFilters((prev) => [...prev, { id: Math.random().toString(36).slice(2), field: "", operator: "=", value: "" }]);
    setAdditionalOpen(true);
  }

  function updateFilter(id: string, key: keyof AdditionalFilter, val: string) {
    setAdditionalFilters((prev) => prev.map((f) => f.id === id ? { ...f, [key]: val } : f));
  }

  function removeFilter(id: string) {
    setAdditionalFilters((prev) => prev.filter((f) => f.id !== id));
  }

  async function handlePreview() {
    // Build filters fresh from local state so we don't depend on the parent
    // prop (which can lag a render behind when the user just selected the mode).
    const effectiveInd = customIndustry.trim() || industry;
    const liveFilters: DmFilter[] = [
      { field: "business_email", operator: "has_value", value: null },
    ];
    if (effectiveInd)
      liveFilters.push({ field: "primary_industry", operator: "in", value: [effectiveInd] });
    if (states.length)
      liveFilters.push({ field: "company_state", operator: "in", value: states });
    if (seniorities.length)
      liveFilters.push({ field: "seniority_level_2", operator: "in", value: seniorities });
    if (sizes.length)
      liveFilters.push({ field: "company_employee_count", operator: "in", value: sizes });
    if (revenues.length)
      liveFilters.push({ field: "company_revenue", operator: "in", value: revenues });
    const codes = naicsCodes.split(",").map((c) => c.trim()).filter(Boolean);
    if (codes.length)
      liveFilters.push({ field: "company_naics", operator: "in", value: codes });
    for (const af of additionalFilters) {
      if (af.field.trim() && af.value.trim()) {
        const vals = af.value.split(",").map((v) => v.trim()).filter(Boolean);
        liveFilters.push({ field: af.field.trim(), operator: af.operator || "=", value: vals.length > 1 ? vals : vals[0] ?? "" });
      }
    }

    if (!effectiveInd) {
      toast.error("Select an industry before previewing");
      return;
    }
    setPreviewing(true);
    setSaveResult(null);
    try {
      // DataMoon preview endpoint caps returned records (typically 25) regardless of limit
      const data = await datamoonApi.previewProspects({ filters: liveFilters, limit: 25 });
      setPreviewTotal(data.total);
      setPreviewProspects(data.prospects);
      setPreviewLiveFilters(liveFilters);
      setPreviewAudienceId(data.audience_id ?? null);
      setPreviewPage(1);
      setPreviewOpen(true);
      if (data.audience_id != null) {
        const parsedLimit = parseInt(limit, 10);
        const fetchLimit = !isNaN(parsedLimit) && parsedLimit > 0 ? parsedLimit : 500;
        onAudiencePreviewReady?.(data.audience_id, fetchLimit);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Preview failed: ${msg}`);
    } finally {
      setPreviewing(false);
    }
  }


  const extraCount = additionalFilters.filter((f) => f.field.trim() && f.value.trim()).length;
  const effectiveIndustry = customIndustry.trim() || industry;
  const industryError = industryTouched && !effectiveIndustry;

  return (
    <div className="space-y-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
      <p className="text-xs font-semibold text-indigo-800 flex items-center gap-1.5">
        <Database className="h-3.5 w-3.5" /> Sourced Prospecting Filters
      </p>

      {/* Quota + Limit block */}
      {dmStatus && (
        <div className="rounded-md border bg-muted/40 px-3 py-2.5 space-y-2">
          {dmStatus.prospect_quota != null && dmStatus.quota_used != null ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-foreground">Monthly Quota</span>
                <span className={quotaRemaining != null && quotaRemaining <= 0 ? "text-destructive font-semibold" : "text-muted-foreground"}>
                  {dmStatus.quota_used.toLocaleString()} / {dmStatus.prospect_quota.toLocaleString()} used
                  {dmStatus.quota_reset_at && (
                    <> · resets {new Date(dmStatus.quota_reset_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>
                  )}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    quotaRemaining != null && quotaRemaining <= 0
                      ? "bg-destructive"
                      : quotaRemaining != null && dmStatus.prospect_quota && quotaRemaining < dmStatus.prospect_quota * 0.2
                      ? "bg-amber-500"
                      : "bg-primary"
                  }`}
                  style={{ width: `${Math.min(100, (dmStatus.quota_used / dmStatus.prospect_quota) * 100)}%` }}
                />
              </div>
              {quotaRemaining != null && quotaRemaining <= 0 && (
                <p className="text-xs text-destructive font-medium">Monthly quota exhausted — contact your administrator.</p>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="text-xs">Quota not configured</Badge>
          )}
          <div className="flex items-center gap-2 pt-0.5">
            <Label htmlFor="icp-dm-limit" className="shrink-0 text-xs font-medium">Max records to fetch</Label>
            <Input
              id="icp-dm-limit"
              type="number"
              min={1}
              max={quotaRemaining ?? 5000}
              className="h-7 w-24 text-sm"
              value={limit}
              onChange={(e) => onLimitChange(e.target.value)}
              placeholder="500"
            />
            {quotaRemaining != null && quotaRemaining > 0 && (
              <span className="text-xs text-muted-foreground">up to {quotaRemaining.toLocaleString()} remaining</span>
            )}
          </div>
        </div>
      )}

      {/* Business Email — always on, shown as badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 text-[11px] px-2.5 py-0.5 font-medium">
          Business Email <span className="text-[10px] font-semibold text-blue-600">Required</span>
        </span>
        <span className="text-[11px] text-muted-foreground">Always applied</span>
      </div>

      {/* Industry — required */}
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1">
          Industry <span className="text-red-500">*</span>
        </Label>
        <select
          className={`w-full h-9 rounded-md border bg-background px-3 text-sm transition-colors ${
            industryError ? "border-red-500 ring-1 ring-red-500" : ""
          } ${customIndustry.trim() ? "opacity-50" : ""}`}
          value={industry}
          onChange={(e) => { setIndustry(e.target.value); setIndustryTouched(true); if (e.target.value) setCustomIndustry(""); }}
          onBlur={() => setIndustryTouched(true)}
          disabled={!!customIndustry.trim()}
        >
          <option value="">Select an industry…</option>
          {DM_INDUSTRY_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground shrink-0">Or type custom:</span>
          <Input
            className="h-8 flex-1 text-sm"
            value={customIndustry}
            onChange={(e) => { setCustomIndustry(e.target.value); setIndustryTouched(true); if (e.target.value) setIndustry(""); }}
            placeholder="e.g. Fintech, PropTech, EdTech…"
          />
        </div>
        {effectiveIndustry && (
          <p className="text-[11px] text-muted-foreground">
            Active: <span className="font-medium text-foreground">{effectiveIndustry}</span>
          </p>
        )}
        {industryError && (
          <p className="text-[11px] text-red-500">Industry is required</p>
        )}
      </div>

      {/* State / Region */}
      <div className="space-y-1.5">
        <Label className="text-xs">State / Region</Label>
        <div className="relative">
          <Input
            className="h-8 text-sm"
            placeholder="Search states…"
            value={stateSearch}
            onChange={(e) => setStateSearch(e.target.value)}
          />
          {stateSearch && (
            <div className="absolute z-20 mt-1 max-h-40 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
              {DM_US_STATES.filter(
                (s) => s.toLowerCase().includes(stateSearch.toLowerCase()) && !states.includes(s)
              ).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => { setStates((prev) => [...prev, s]); setStateSearch(""); }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        {states.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {states.map((s) => (
              <span key={s} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-800 text-[11px] px-2 py-0.5">
                {s}
                <button type="button" onClick={() => setStates((prev) => prev.filter((x) => x !== s))} className="hover:text-red-600">×</button>
              </span>
            ))}
            <button type="button" className="text-[11px] text-muted-foreground underline" onClick={() => setStates([])}>Clear all</button>
          </div>
        )}
      </div>

      {/* Seniority */}
      <div className="space-y-1.5">
        <Label className="text-xs">Target Seniority</Label>
        <div className="flex flex-wrap gap-1.5">
          {DM_SENIORITY_OPTIONS.map(({ value: v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => setSeniorities((prev) => toggleArr(prev, v))}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                seniorities.includes(v)
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Company Size */}
      <div className="space-y-1.5">
        <Label className="text-xs">Company Size (employees)</Label>
        <div className="flex flex-wrap gap-1.5">
          {DM_SIZE_OPTIONS.map(({ value: v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => setSizes((prev) => toggleArr(prev, v))}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                sizes.includes(v)
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Company Revenue */}
      <div className="space-y-1.5">
        <Label className="text-xs">Company Revenue</Label>
        <div className="flex flex-wrap gap-1.5">
          {DM_REVENUE_OPTIONS.map(({ value: v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => setRevenues((prev) => toggleArr(prev, v))}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                revenues.includes(v)
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* NAICS Code — with industry-aware suggestions */}
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1">
          Company NAICS Code
          <span className="text-muted-foreground font-normal">(comma-separated)</span>
        </Label>

        {/* Suggestions based on selected industry */}
        {effectiveIndustry && INDUSTRY_NAICS_MAP[effectiveIndustry] && (
          <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2.5 space-y-1.5">
            <p className="text-[11px] font-medium text-indigo-700">
              💡 Suggested for <span className="font-semibold">{effectiveIndustry}</span> — click to add:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {INDUSTRY_NAICS_MAP[effectiveIndustry].map(({ code, title }) => {
                const selected = naicsCodes.split(",").map((c) => c.trim()).includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    title={title}
                    onClick={() => {
                      const current = naicsCodes.split(",").map((c) => c.trim()).filter(Boolean);
                      if (selected) {
                        setNaicsCodes(current.filter((c) => c !== code).join(", "));
                      } else {
                        setNaicsCodes([...current, code].join(", "));
                      }
                    }}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono font-medium transition-colors ${
                      selected
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-100"
                    }`}
                  >
                    {selected && <span className="text-[10px]">✓</span>}
                    {code}
                    <span className={`font-sans font-normal text-[10px] ${selected ? "text-indigo-200" : "text-muted-foreground"}`}>
                      {title.length > 30 ? title.slice(0, 28) + "…" : title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Manual input — always shown as fallback */}
        <Input
          className="h-8 text-sm font-mono"
          placeholder={effectiveIndustry && INDUSTRY_NAICS_MAP[effectiveIndustry] ? "Or type additional codes…" : "e.g. 541511, 541512"}
          value={naicsCodes}
          onChange={(e) => setNaicsCodes(e.target.value)}
        />
        {!effectiveIndustry && (
          <p className="text-[11px] text-muted-foreground">
            Select an industry above to see suggested NAICS codes.
          </p>
        )}
        {effectiveIndustry && !INDUSTRY_NAICS_MAP[effectiveIndustry] && (
          <p className="text-[11px] text-muted-foreground">
            No suggestions for this industry.{" "}
            <a
              href="https://www.census.gov/naics/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 underline hover:text-indigo-800"
            >
              Look up NAICS codes →
            </a>
          </p>
        )}
      </div>

      {/* Additional Filters accordion */}
      <div className="rounded-md border border-indigo-200 bg-white/60">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-indigo-800 hover:bg-indigo-50/60 transition-colors"
          onClick={() => setAdditionalOpen((o) => !o)}
        >
          <span className="flex items-center gap-1.5">
            Additional Filters
            {extraCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 font-semibold leading-none">
                {extraCount}
              </span>
            )}
          </span>
          <span className="text-muted-foreground text-xs">{additionalOpen ? "▲" : "▼"}</span>
        </button>

        {additionalOpen && (
          <div className="px-3 pb-3 space-y-2 border-t border-indigo-100 pt-2">
            {additionalFilters.map((af) => (
              <div key={af.id} className="flex items-center gap-2">
                {/* Field dropdown */}
                <select
                  className="h-8 rounded-md border bg-background px-2 text-xs flex-1"
                  value={af.field}
                  onChange={(e) => updateFilter(af.id, "field", e.target.value)}
                >
                  <option value="">Select field…</option>
                  {DM_ADDITIONAL_FIELD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {/* Operator dropdown */}
                <select
                  className="h-8 rounded-md border bg-background px-2 text-xs w-28"
                  value={af.operator}
                  onChange={(e) => updateFilter(af.id, "operator", e.target.value)}
                >
                  {DM_OPERATOR_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {/* Value input */}
                <Input
                  className="h-8 text-xs flex-1"
                  placeholder="value"
                  value={af.value}
                  onChange={(e) => updateFilter(af.id, "value", e.target.value)}
                />
                {/* Remove */}
                <button
                  type="button"
                  className="flex-shrink-0 rounded-md border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 w-7 h-8 flex items-center justify-center text-sm"
                  onClick={() => removeFilter(af.id)}
                >
                  🗑
                </button>
              </div>
            ))}
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 rounded-md px-3 py-1.5 hover:bg-indigo-50 transition-colors"
              onClick={addFilter}
            >
              <span className="text-sm leading-none font-bold">+</span> Add filter
            </button>
          </div>
        )}
      </div>

      {/* Preview Prospects button */}
      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          onClick={handlePreview}
          disabled={previewing}
        >
          {previewing ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Search className="h-3.5 w-3.5 mr-1.5" />
          )}
          {previewing ? "Loading preview…" : "Preview Prospects"}
        </Button>
      </div>

      {/* Preview results — rendered inline to avoid nested-Dialog issues */}
      {previewOpen && (() => {
        const totalPages = Math.max(1, Math.ceil(previewProspects.length / PAGE_SIZE));
        const pageProspects = previewProspects.slice((previewPage - 1) * PAGE_SIZE, previewPage * PAGE_SIZE);
        return (
          <div className="rounded-lg border border-indigo-200 bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 border-b border-indigo-100">
              <p className="text-xs text-indigo-800">
                {previewTotal != null && previewTotal > 0 ? (
                  <>
                    <span className="font-semibold">~{previewTotal.toLocaleString()} prospects</span> match your filters. Choose how many to fetch
                    {quotaRemaining != null && (
                      <>, up to{" "}
                        <span className={quotaRemaining <= 0 ? "text-destructive font-semibold" : "font-semibold"}>
                          {quotaRemaining.toLocaleString()}
                        </span>{" "}
                        remaining in your monthly quota
                      </>
                    )}.
                  </>
                ) : "No prospects found for these filters"}
              </p>
              <button
                type="button"
                className="text-xs text-indigo-500 hover:text-indigo-800 font-medium"
                onClick={() => setPreviewOpen(false)}
              >
                ✕ Close
              </button>
            </div>

            {previewProspects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2 text-center px-4">
                <AlertCircle className="h-7 w-7 text-amber-400" />
                <p className="text-sm font-medium">No prospects found</p>
                <p className="text-xs text-muted-foreground">
                  Try broadening your filters — e.g. remove a state filter or select more seniority levels.
                </p>
              </div>
            ) : (
              <>
                <table className="w-full text-xs border-collapse table-fixed">
                  <colgroup>
                    <col className="w-7" />
                    <col className="w-[22%]" />
                    <col className="w-[30%]" />
                    <col className="w-[25%]" />
                    <col className="w-[16%]" />
                  </colgroup>
                  <thead className="bg-indigo-50 border-b border-indigo-100">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold text-indigo-700">#</th>
                      <th className="text-left px-2 py-2 font-semibold text-indigo-700">Name</th>
                      <th className="text-left px-2 py-2 font-semibold text-indigo-700">Email</th>
                      <th className="text-left px-2 py-2 font-semibold text-indigo-700">Title</th>
                      <th className="text-left px-2 py-2 font-semibold text-indigo-700">Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageProspects.map((p, i) => {
                      const rowNum = (previewPage - 1) * PAGE_SIZE + i + 1;
                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-indigo-50/40">
                          <td className="px-2 py-2 text-muted-foreground">{rowNum}</td>
                          <td className="px-2 py-2 font-medium truncate">
                            {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                          </td>
                          <td className="px-2 py-2 text-blue-700 font-mono truncate">{p.business_email || "—"}</td>
                          <td className="px-2 py-2 text-muted-foreground truncate">{p.job_title || "—"}</td>
                          <td className="px-2 py-2 truncate">
                            <span className="font-medium">{p.company_name || p.company_domain || "—"}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-indigo-100 bg-indigo-50/50">
                    <span className="text-xs text-muted-foreground">
                      Page {previewPage} of {totalPages} · {previewProspects.length} records
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={previewPage <= 1}
                        onClick={() => setPreviewPage((p) => p - 1)}
                        className="h-6 w-6 flex items-center justify-center rounded border border-indigo-200 text-indigo-600 disabled:opacity-30 hover:bg-indigo-100"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                        .filter((pg) => pg === 1 || pg === totalPages || Math.abs(pg - previewPage) <= 1)
                        .reduce<(number | "…")[]>((acc, pg, i, arr) => {
                          if (i > 0 && pg - (arr[i - 1] as number) > 1) acc.push("…");
                          acc.push(pg);
                          return acc;
                        }, [])
                        .map((pg, i) =>
                          pg === "…" ? (
                            <span key={`ellipsis-${i}`} className="text-xs text-muted-foreground px-1">…</span>
                          ) : (
                            <button
                              key={pg}
                              type="button"
                              onClick={() => setPreviewPage(pg as number)}
                              className={`h-6 min-w-[24px] px-1.5 text-xs rounded border ${
                                previewPage === pg
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                              }`}
                            >
                              {pg}
                            </button>
                          )
                        )}
                      <button
                        type="button"
                        disabled={previewPage >= totalPages}
                        onClick={() => setPreviewPage((p) => p + 1)}
                        className="h-6 w-6 flex items-center justify-center rounded border border-indigo-200 text-indigo-600 disabled:opacity-30 hover:bg-indigo-100"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Save result banner */}
                {saveResult && (
                  saveResult.imported > 0 ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border-t border-emerald-200 text-xs text-emerald-800">
                      <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span>
                        <b>{saveResult.imported}</b> prospect{saveResult.imported !== 1 ? "s" : ""} saved to your database
                        {saveResult.skipped > 0 && <> · <b>{saveResult.skipped}</b> skipped (duplicates)</>}.
                        Go to <b>Prospects</b> page to view them.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-800">
                      <span className="shrink-0">⚠️</span>
                      <span>
                        All {saveResult.skipped > 0 ? <><b>{saveResult.skipped}</b> prospect{saveResult.skipped !== 1 ? "s" : ""}</> : "prospects"} already exist in your database — no new records added.
                      </span>
                    </div>
                  )
                )}

                {/* Save Prospects button removed — prospects are now imported automatically on Save ICP */}
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ── IcpFormDialog ──────────────────────────────────────────────────── */

function IcpFormDialog({
  form,
  setForm,
  editingId,
  suggesting,
  saving,
  hasLlm,
  onAiSuggest,
  onSave,
  onClose,
  onAudiencePreviewReady,
}: {
  form: IcpFormState;
  setForm: React.Dispatch<React.SetStateAction<IcpFormState>>;
  editingId: string | null;
  suggesting: boolean;
  saving: boolean;
  hasLlm: boolean;
  onAiSuggest: () => void;
  onSave: () => void;
  onClose: () => void;
  onAudiencePreviewReady: (audienceId: number, limit: number) => void;
}) {
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [step1Error, setStep1Error] = React.useState<string | null>(null);
  // Must preview prospects before Save ICP is allowed (datamoon mode)
  const [hasPreview, setHasPreview] = React.useState(false);

  // Reset to step 1 whenever dialog opens (editingId changes or form resets)
  React.useEffect(() => {
    setStep(1);
    setStep1Error(null);
    setHasPreview(false);
  }, [editingId]);

  function f(key: keyof IcpFormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  const totalSteps = form.prospectMode === "datamoon" ? 3 : 2;

  function handleNext() {
    if (step === 1) {
      if (!form.name.trim()) {
        setStep1Error("Profile name is required.");
        return;
      }
      setStep1Error(null);
      setStep(2);
    } else if (step === 2) {
      if (form.prospectMode === "datamoon") {
        setStep(3);
      } else {
        // non-datamoon: step 2 is the last step — save directly
        onSave();
      }
    }
  }

  function handleBack() {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  }

  const isLastStep = step === totalSteps;

  // Step indicator dots
  const StepIndicator = () => (
    <div className="flex items-center gap-2 mb-4">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
        <React.Fragment key={s}>
          <div
            className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-colors ${
              s < step
                ? "bg-indigo-600 border-indigo-600 text-white"
                : s === step
                ? "border-indigo-600 text-indigo-600 bg-indigo-50"
                : "border-muted-foreground/30 text-muted-foreground/50 bg-background"
            }`}
          >
            {s < step ? "✓" : s}
          </div>
          {s < totalSteps && (
            <div className={`flex-1 h-0.5 ${s < step ? "bg-indigo-600" : "bg-muted"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {editingId ? "Edit ICP Profile" : "Create ICP Profile"}
        </DialogTitle>
        <DialogDescription>
          {step === 1 && "Step 1 of " + totalSteps + " — ICP Details"}
          {step === 2 && "Step 2 of " + totalSteps + " — Prospect Mode"}
          {step === 3 && "Step 3 of " + totalSteps + " — Sourced Prospecting Filters"}
        </DialogDescription>
      </DialogHeader>

      <div className="py-4">
        <StepIndicator />

        {/* ── Step 1: ICP Details ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Core fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Profile Name *</Label>
                <Input
                  placeholder="e.g. SaaS CTOs under 200"
                  value={form.name}
                  onChange={(e) => {
                    f("name")(e);
                    if (e.target.value.trim()) setStep1Error(null);
                  }}
                />
                {step1Error && (
                  <p className="text-xs text-destructive">{step1Error}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Company Type</Label>
                <Input
                  placeholder="e.g. B2B SaaS, Series A–C"
                  value={form.companyType}
                  onChange={f("companyType")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Persona</Label>
              <Textarea
                placeholder="Describe your ideal buyer in detail…"
                value={form.persona}
                onChange={f("persona")}
                rows={3}
              />
            </div>

            <Separator />
            <p className="text-sm font-medium">Sender Information</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Role</Label>
                <Input
                  placeholder="e.g. CEO at MyCo"
                  value={form.senderRole}
                  onChange={f("senderRole")}
                />
              </div>
              <div className="space-y-2">
                <Label>Your Company</Label>
                <Input
                  placeholder="e.g. MyCo"
                  value={form.senderCompany}
                  onChange={f("senderCompany")}
                />
              </div>
              <div className="space-y-2">
                <Label>Your Offer</Label>
                <Input
                  placeholder="e.g. AI lead gen platform"
                  value={form.senderOffer}
                  onChange={f("senderOffer")}
                />
              </div>
              <div className="space-y-2">
                <Label>Proof Metric</Label>
                <Input
                  placeholder="e.g. 3× pipeline in 90 days"
                  value={form.proofMetric}
                  onChange={f("proofMetric")}
                />
              </div>
            </div>

            <Separator />
            <p className="text-sm font-medium">Top 3 Objections</p>
            <p className="text-xs text-muted-foreground">
              What will this buyer push back on when approached?
            </p>
            <div className="grid grid-cols-1 gap-2">
              <Input
                placeholder='e.g. "We already have a solution for this"'
                value={form.objection1}
                onChange={f("objection1")}
              />
              <Input
                placeholder="e.g. &quot;We don't have budget for new tools right now&quot;"
                value={form.objection2}
                onChange={f("objection2")}
              />
              <Input
                placeholder='e.g. "Our team is too busy to evaluate another vendor"'
                value={form.objection3}
                onChange={f("objection3")}
              />
            </div>

            <Separator />
            <p className="text-sm font-medium">Pain Points</p>
            <p className="text-xs text-muted-foreground">
              What daily frustrations or strategic gaps does this buyer face?
            </p>
            <div className="grid grid-cols-1 gap-2">
              <Input
                placeholder='e.g. "Manual prospecting takes 20+ hours per week"'
                value={form.pain1}
                onChange={f("pain1")}
              />
              <Input
                placeholder='e.g. "Low reply rates on outbound campaigns (under 2%)"'
                value={form.pain2}
                onChange={f("pain2")}
              />
              <Input
                placeholder='e.g. "Sales reps send generic templates that damage brand"'
                value={form.pain3}
                onChange={f("pain3")}
              />
            </div>

            <Separator />
            <p className="text-sm font-medium">Value Propositions</p>
            <p className="text-xs text-muted-foreground">
              What specific outcomes can you deliver? Use metrics when possible.
            </p>
            <div className="grid grid-cols-1 gap-2">
              <Input
                placeholder='e.g. "3× more qualified pipeline in 90 days"'
                value={form.value1}
                onChange={f("value1")}
              />
              <Input
                placeholder='e.g. "Cut prospecting time from 20 hrs to 2 hrs per week"'
                value={form.value2}
                onChange={f("value2")}
              />
              <Input
                placeholder='e.g. "AI-written emails that sound human, not like a vendor"'
                value={form.value3}
                onChange={f("value3")}
              />
            </div>
          </div>
        )}

        {/* ── Step 2: Prospect Mode ────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Prospect Mode</Label>
              <p className="text-xs text-muted-foreground">How will prospects be sourced for this ICP?</p>
              <div className="grid grid-cols-2 gap-2">
                {PROSPECT_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, prospectMode: mode.value }))}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      form.prospectMode === mode.value
                        ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                        : "border-border bg-background hover:bg-accent"
                    }`}
                  >
                    <p className={`text-xs font-semibold ${form.prospectMode === mode.value ? "text-indigo-700" : ""}`}>
                      {mode.label}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{mode.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {form.prospectMode !== "datamoon" && (
              <div className="rounded-md bg-muted/40 border px-4 py-3 text-xs text-muted-foreground">
                {form.prospectMode === "manual"
                  ? "You'll add prospects manually after saving the ICP."
                  : "Web scraping will be configured after saving the ICP."}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: DataMoon Filters (datamoon mode only) ─────────────── */}
        {step === 3 && (
          <DataMoonFiltersForm
            filters={form.datamoonFilters}
            onChange={(f) => {
              // Any filter change invalidates the current preview
              setHasPreview(false);
              setForm((prev) => ({ ...prev, datamoonFilters: f }));
            }}
            limit={form.datamoonLimit}
            onLimitChange={(v) => {
              // Limit change also invalidates preview
              setHasPreview(false);
              setForm((prev) => ({ ...prev, datamoonLimit: v }));
            }}
            icpId={editingId}
            onAudiencePreviewReady={(audienceId, limit) => {
              setHasPreview(true); // unlock Save ICP once preview succeeds
              onAudiencePreviewReady(audienceId, limit);
            }}
          />
        )}
      </div>

      <DialogFooter className="flex-col sm:flex-row gap-2">
        <div className="flex gap-2 flex-1">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} disabled={saving}>
              ← Back
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          {step === 1 && (
            <Button
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={onAiSuggest}
              disabled={suggesting || !form.persona.trim() || !hasLlm}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {suggesting ? "Suggesting…" : "AI Suggest Fields"}
            </Button>
          )}

          {isLastStep ? (
            <div className="flex flex-col items-end gap-1">
              {step === 3 && !hasPreview && !saving && (
                <p className="text-xs text-amber-600">Preview prospects first to enable saving.</p>
              )}
              <Button
                onClick={onSave}
                disabled={saving || (step === 3 && !hasPreview)}
                title={step === 3 && !hasPreview ? "Run a preview first to confirm your filters" : undefined}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : null}
                {saving
                  ? form.prospectMode === "datamoon"
                    ? "Saving & importing…"
                    : "Saving…"
                  : editingId
                  ? "Update ICP"
                  : "Save ICP"}
              </Button>
            </div>
          ) : (
            <Button onClick={handleNext}>
              Next →
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
}

/* ── DiscoverResult ─────────────────────────────────────────────────── */

interface DiscoverResultShape {
  suggestedPersona?: string;
  commonAttributes?: Record<string, unknown>;
}

function DiscoverResult({
  result,
  discoverMode,
  discoverSave,
  discoverWebsite,
  onSaveProfile,
}: {
  result: DiscoverResultShape;
  discoverMode: "website" | "description";
  discoverSave: boolean;
  discoverWebsite: string;
  onSaveProfile: (p: {
    name?: string;
    persona?: string;
    companyType?: string | null;
  }) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border p-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          Discovery Result
        </p>
        <Badge variant="outline" className="text-xs font-mono">
          {discoverMode === "website" ? (
            <Globe className="h-3 w-3 mr-1" />
          ) : (
            <FileText className="h-3 w-3 mr-1" />
          )}
          {discoverMode === "website" ? discoverWebsite : "your description"}
        </Badge>
      </div>

      {/* Suggested persona */}
      {result.suggestedPersona && (
        <div className="text-xs space-y-1">
          <p>
            <span className="font-medium">Suggested Persona:</span>{" "}
            {`${result.suggestedPersona}`}
          </p>
        </div>
      )}

      {/* Common attributes */}
      {result.commonAttributes && Object.keys(result.commonAttributes).length > 0 && (
          <div className="text-xs space-y-1">
            <p className="font-medium">Common Attributes:</p>
            {Object.entries(result.commonAttributes).map(
              ([k, v]) => (
                <p key={k}>
                  <span className="font-medium capitalize">{k}:</span>{" "}
                  {`${v}`}
                </p>
              )
            )}
          </div>
        )}

      {/* Review mode save button */}
      {!discoverSave && (
        <Button
          size="sm"
          className="mt-2"
          onClick={() =>
            onSaveProfile({
              name:
                discoverMode === "website"
                  ? `Profile from ${discoverWebsite}`
                  : "Auto-Discovered ICP",
              persona: result.suggestedPersona
                ? String(result.suggestedPersona)
                : undefined,
              companyType:
                result.commonAttributes && "companyType" in result.commonAttributes
                  ? `${result.commonAttributes.companyType}`
                  : null,
            })
          }
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Save {"&"} Close
        </Button>
      )}

      {discoverSave && (
        <p className="text-xs text-emerald-700 font-medium">
          ✓ Profile saved — modal will close shortly.
        </p>
      )}
    </div>
  );
}